import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  ShieldIcon,
  DownloadIcon,
  RotateCcwIcon,
  CalendarIcon,
  ClockIcon,
  HardDriveIcon,
  CheckCircleIcon,
  AlertTriangleIcon,
  XCircleIcon,
  PlayIcon,
  PlusIcon,
  SearchIcon,
  Trash2Icon,
  EyeIcon,
  XIcon,
  ServerIcon,
  DatabaseIcon,
  FileArchiveIcon,
  ZapIcon,
  RefreshCwIcon,
  InfoIcon,
  Edit3Icon,
  LoaderIcon,
  AlertCircleIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DollarSignIcon,
  BarChart3Icon,
  ArchiveIcon,
} from 'lucide-react'
import { useState, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  AreaChart,
} from 'recharts'
import { toast } from 'sonner'

import { ResponsivePageHeader, ResponsiveTabs } from '@nebula/shared/components'
import { ConfirmModal } from '@nebula/shared/components/modals'
import { usePermissions } from '@nebula/shared/hooks'
import {
  backupsApi,
  formatBytes,
  formatDuration,
  getBackupStatusLabel,
  type BackupSnapshot,
  type BackupJob,
  type BackupJobVM,
  type CreateBackupJobRequest,
  type InitiateRestoreRequest,
  type BackupRestoreJob,
  backupAlertsApi,
  getAlertTypeLabel,
  getAlertSeverityLabel,
  getAlertStatusLabel,
  getAlertSeverityColor,
  getAlertStatusColor,
  getAlertSeverityIcon,
  formatAlertDate,
  formatGrowthPercentage,
  formatStorageGB,
  type BackupUsageAlert,
  type AlertStatus,
  type AlertSeverity,
} from '@nebula/shared/services'
import { apiClient } from '@nebula/shared/services/api'
import {
  getErrorMessage,
  isBackupConcluido,
  FILTRO_DE_BACKUP_CONCLUIDO,
} from '@nebula/shared/utils'
import { isVMRemovida } from '@nebula/shared/utils/vmRemovida'

type BackupUsageSummary = {
  total_storage_gb?: number
  avg_storage_gb?: number
  max_storage_gb?: number
  total_cost: number
  total_vms_with_backup?: number
  total_vms?: number
  total_days?: number
  billing_period: string
  currency: string
  price_per_gb?: number
  breakdown_by_vm?: {
    vm_id: string
    vm_name: string
    total_storage_gb: number
    total_cost: number
    snapshot_count: number
  }[]
}

type DailyUsageRecord = {
  date: string
  storage_gb: number
  cost: number
  snapshots: number
}

type TabKey = 'jobs' | 'snapshots' | 'logs' | 'usage' | 'alerts'
const VALID_TABS = new Set<TabKey>(['usage', 'snapshots', 'jobs', 'logs', 'alerts'])

const BackupsPage = () => {
  const queryClient = useQueryClient()
  const { has } = usePermissions()
  // Reconhecer/resolver/descartar alerta é mutação → exige backup:write no backend.
  // Sem o gate, o Visualizador veria o botão e levaria 403 ao clicar.
  const canManageAlerts = has('backup:write')
  const [searchParams, setSearchParams] = useSearchParams()
  const tabParam = searchParams.get('tab') as TabKey | null
  const activeTab: TabKey = tabParam && VALID_TABS.has(tabParam) ? tabParam : 'usage'
  const setActiveTab = useCallback(
    (tab: TabKey) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          if (tab === 'usage') next.delete('tab')
          else next.set('tab', tab)
          return next
        },
        { replace: true },
      )
    },
    [setSearchParams],
  )
  const [searchTerm, setSearchTerm] = useState('')
  const [showRestoreModal, setShowRestoreModal] = useState(false)
  const [showDetailsModal, setShowDetailsModal] = useState(false)
  const [selectedSnapshot, setSelectedSnapshot] = useState<BackupSnapshot | null>(null)
  const [vmFilter, _setVmFilter] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [operationTypeFilter, setOperationTypeFilter] = useState<'all' | 'backup' | 'restore'>(
    'all',
  )
  const [autoRefresh, setAutoRefresh] = useState(true)
  const [expandedVMs, setExpandedVMs] = useState<Set<string>>(new Set())

  // Job state
  const [showJobModal, setShowJobModal] = useState(false)
  const [editingJob, setEditingJob] = useState<BackupJob | null>(null)
  const [expandedJobs, setExpandedJobs] = useState<Set<string>>(new Set())
  const [expandedRemovedVMs, setExpandedRemovedVMs] = useState<Set<string>>(new Set())
  const [jobToDelete, setJobToDelete] = useState<{ id: string; name: string } | null>(null)
  const [executingJobId, setExecutingJobId] = useState<string | null>(null)
  const [jobSearchTerm, setJobSearchTerm] = useState('')

  // Job form state
  const [jobForm, setJobForm] = useState<CreateBackupJobRequest>({
    name: '',
    cron_expression: '0 2 * * *',
    timezone: 'America/Sao_Paulo',
    backup_type: 'snapshot',
    compression_level: 'balanced',
    encryption_enabled: true,
    verification_enabled: true,
    keep_last: 7,
    keep_daily: 7,
    keep_weekly: 4,
    keep_monthly: 12,
    stop_on_failure: false,
    notify_on_failure: true,
    notification_emails: [],
    vm_ids: [],
  })

  // Job frequency helper state
  const [jobFrequency, setJobFrequency] = useState<'daily' | 'weekly' | 'monthly'>('daily')
  const [jobTime, setJobTime] = useState('02:00')
  const [jobWeekDay, setJobWeekDay] = useState('0')
  const [jobRetentionPreset, setJobRetentionPreset] = useState<
    '7days' | '15days' | '30days' | '60days' | '90days' | 'custom'
  >('30days')
  const [showJobAdvancedRetention, setShowJobAdvancedRetention] = useState(false)
  const [snapshotToDelete, setSnapshotToDelete] = useState<string | null>(null)
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false)

  // Usage tab state
  const [selectedPeriod, setSelectedPeriod] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`
  })

  // Alerts tab state
  const [alertsStatusFilter, setAlertsStatusFilter] = useState<AlertStatus | undefined>(undefined)
  const [alertsSeverityFilter, setAlertsSeverityFilter] = useState<AlertSeverity | undefined>(
    undefined,
  )
  const [selectedAlert, setSelectedAlert] = useState<BackupUsageAlert | null>(null)
  const [showAlertDetailsModal, setShowAlertDetailsModal] = useState(false)
  const [showResolveModal, setShowResolveModal] = useState(false)
  const [resolutionNotes, setResolutionNotes] = useState('')

  // Restore form state
  const [restoreForm, setRestoreForm] = useState<InitiateRestoreRequest>({
    snapshot_id: '',
    restore_type: 'full',
    target_vm_id: '',
    new_vm_name: '',
    start_after_restore: true,
    replace_existing: true, // Sempre true para restauração completa (implícito)
    restore_network_config: true,
  })

  // ========================================
  // API Queries
  // ========================================

  // Fetch customer VMs
  const { data: customerVMs = [], isLoading: vmsLoading } = useQuery({
    queryKey: ['customer-vms'],
    queryFn: () => backupsApi.listCustomerVMs(),
  })

  // Fetch snapshots
  const {
    data: snapshots = [],
    isLoading: snapshotsLoading,
    refetch: refetchSnapshots,
    dataUpdatedAt: snapshotsUpdatedAt,
  } = useQuery({
    queryKey: ['backup-snapshots', vmFilter, statusFilter],
    queryFn: () =>
      backupsApi.listSnapshots({
        vm_id: vmFilter || undefined,
        status: statusFilter || undefined,
      }),
  })

  // Fetch backup jobs
  const {
    data: backupJobs = [],
    isLoading: jobsLoading,
    refetch: refetchJobs,
  } = useQuery({
    queryKey: ['backup-jobs'],
    queryFn: () => backupsApi.listJobs(),
  })

  // Fetch restore jobs
  // O `statusFilter` é o do select de SNAPSHOTS e pode trazer o valor sintético
  // `concluido`, que só o endpoint de snapshots entende — restore job não tem
  // esse estado. Mandá-lo aqui devolveria lista vazia sem erro.
  const statusDeRestore = statusFilter === FILTRO_DE_BACKUP_CONCLUIDO ? undefined : statusFilter
  const { data: restoreJobs = [], refetch: refetchRestores } = useQuery({
    queryKey: ['backup-restores', statusDeRestore],
    queryFn: () =>
      backupsApi.listRestoreJobs({
        status: statusDeRestore || undefined,
      }),
  })

  // Fetch backup usage summary
  const { data: usageSummary, isLoading: usageLoading } = useQuery<BackupUsageSummary>({
    queryKey: ['backup-usage-summary', selectedPeriod],
    enabled: activeTab === 'usage' && !!selectedPeriod,
    queryFn: async () => {
      const data = await apiClient.get<BackupUsageSummary>(
        `/api/v1/backup-usage/usage/summary?billing_period=${selectedPeriod}`,
      )
      return data
    },
  })

  // Fetch daily usage history for chart
  const { data: dailyUsage = [] } = useQuery<DailyUsageRecord[]>({
    queryKey: ['backup-usage-daily', selectedPeriod],
    enabled: activeTab === 'usage' && !!selectedPeriod,
    queryFn: () =>
      apiClient.get(`/api/v1/backup-usage/usage/daily-history?billing_period=${selectedPeriod}`),
  })

  // Fetch backup usage alerts
  const { data: alertsData, isLoading: alertsLoading } = useQuery({
    queryKey: ['backup-alerts', alertsStatusFilter, alertsSeverityFilter],
    enabled: activeTab === 'alerts',
    queryFn: () =>
      backupAlertsApi.listBackupAlerts({
        status_filter: alertsStatusFilter,
        severity_filter: alertsSeverityFilter,
        limit: 50,
      }),
  })

  const alerts = alertsData?.alerts || []

  // Fetch activity logs
  const {
    data: activityLogs = [],
    isLoading: activityLogsLoading,
    refetch: refetchActivityLogs,
  } = useQuery({
    queryKey: ['backup-activity-logs'],
    enabled: activeTab === 'logs',
    queryFn: () => backupsApi.listActivityLogs({ limit: 100 }),
  })

  // ========================================
  // Auto-refresh for in-progress backups
  // ========================================
  useEffect(() => {
    // Check if there are any snapshots in progress
    const hasInProgressSnapshots = snapshots.some(
      (s) => s.status === 'running' || s.status === 'pending',
    )

    const hasInProgressRestores = restoreJobs.some(
      (r) => r.status === 'running' || r.status === 'pending',
    )

    // Keep polling while any job still reports "running" — the backend
    // commits the snapshot status BEFORE updating the job status, so
    // snapshots may finish while the job is still "running" briefly.
    const hasRunningJobs = backupJobs.some((j) => j.last_run_status === 'running')

    if (hasInProgressSnapshots || hasInProgressRestores || hasRunningJobs) {
      // Auto-refresh every 5 seconds (reduced from 3s to avoid pool exhaustion)
      const interval = setInterval(() => {
        if (hasInProgressSnapshots || hasRunningJobs) {
          refetchSnapshots()
          refetchJobs()
        }
        if (hasInProgressRestores) {
          refetchRestores()
        }
      }, 5000)

      return () => clearInterval(interval)
    }
  }, [
    snapshots,
    restoreJobs,
    backupJobs,
    refetchSnapshots,
    refetchRestores,
    refetchActivityLogs,
    refetchJobs,
  ])

  // ========================================
  // Helper Functions
  // ========================================

  // Check if VM has backup in progress
  const hasBackupInProgress = (vmId: string, jobId?: string): boolean => {
    return snapshots.some(
      (s) =>
        s.vm_id === vmId &&
        (s.status === 'running' || s.status === 'pending') &&
        (jobId ? s.job_id === jobId : true),
    )
  }

  const getBackupInProgress = (vmId: string, jobId?: string): BackupSnapshot | undefined => {
    return snapshots.find(
      (s) =>
        s.vm_id === vmId &&
        (s.status === 'running' || s.status === 'pending') &&
        (jobId ? s.job_id === jobId : true),
    )
  }

  // Check if snapshot has restore in progress
  const getRestoreInProgress = (snapshotId: string): BackupRestoreJob | undefined => {
    return restoreJobs.find(
      (r) => r.snapshot_id === snapshotId && (r.status === 'running' || r.status === 'pending'),
    )
  }

  // Group snapshots by VM
  const groupSnapshotsByVM = (snapshots: BackupSnapshot[]) => {
    const grouped = new Map<
      string,
      { vm_id: string; vm_name: string; snapshots: BackupSnapshot[] }
    >()

    snapshots.forEach((snapshot) => {
      const vmKey = snapshot.vm_id
      if (!grouped.has(vmKey)) {
        grouped.set(vmKey, {
          vm_id: snapshot.vm_id,
          vm_name: snapshot.vm_name || `VM ${snapshot.vm_id}`,
          snapshots: [],
        })
      }
      grouped.get(vmKey)!.snapshots.push(snapshot)
    })

    return Array.from(grouped.values()).sort((a, b) => a.vm_name.localeCompare(b.vm_name))
  }

  const toggleVMExpand = (vmId: string) => {
    setExpandedVMs((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(vmId)) {
        newSet.delete(vmId)
      } else {
        newSet.add(vmId)
      }
      return newSet
    })
  }

  const toggleJobExpand = (jobId: string) => {
    setExpandedJobs((prev) => {
      const newSet = new Set(prev)
      if (newSet.has(jobId)) newSet.delete(jobId)
      else newSet.add(jobId)
      return newSet
    })
  }

  const toggleRemovedVMs = (jobId: string) => {
    setExpandedRemovedVMs((prev) => {
      const next = new Set(prev)
      if (next.has(jobId)) next.delete(jobId)
      else next.add(jobId)
      return next
    })
  }

  // Generate cron for job
  const generateJobCronExpression = () => {
    const [hour, minute] = jobTime.split(':')
    switch (jobFrequency) {
      case 'daily':
        return `${minute} ${hour} * * *`
      case 'weekly':
        return `${minute} ${hour} * * ${jobWeekDay}`
      case 'monthly':
        return `${minute} ${hour} 1 * *`
      default:
        return '0 2 * * *'
    }
  }

  // Apply retention preset to job form
  const applyJobRetentionPreset = (preset: typeof jobRetentionPreset) => {
    const presets: Record<string, Partial<CreateBackupJobRequest>> = {
      '7days': { keep_last: 7, keep_daily: 7, keep_weekly: 0, keep_monthly: 0 },
      '15days': { keep_last: 15, keep_daily: 15, keep_weekly: 2, keep_monthly: 0 },
      '30days': { keep_last: 30, keep_daily: 30, keep_weekly: 4, keep_monthly: 1 },
      '60days': { keep_last: 60, keep_daily: 30, keep_weekly: 8, keep_monthly: 2 },
      '90days': { keep_last: 90, keep_daily: 30, keep_weekly: 12, keep_monthly: 3 },
    }
    if (presets[preset]) {
      setJobForm((prev: CreateBackupJobRequest) => ({ ...prev, ...presets[preset] }))
    }
  }

  // Update job cron expression when frequency/time changes
  useEffect(() => {
    const cron = generateJobCronExpression()
    setJobForm((prev: CreateBackupJobRequest) => ({ ...prev, cron_expression: cron }))
  }, [jobFrequency, jobTime, jobWeekDay])

  // Update job retention when preset changes
  useEffect(() => {
    if (jobRetentionPreset !== 'custom') {
      applyJobRetentionPreset(jobRetentionPreset)
    }
  }, [jobRetentionPreset])

  // Get job aggregates from snapshots
  const getJobAggregates = (jobId: string) => {
    const jobSnapshots = snapshots.filter((s) => s.job_id === jobId)
    const completedSnapshots = jobSnapshots.filter((s) => isBackupConcluido(s.status))
    const totalSizeBytes = jobSnapshots.reduce((acc, s) => acc + (s.size_bytes ?? 0), 0)
    return {
      restorePointCount: completedSnapshots.length,
      totalSizeGB: totalSizeBytes > 0 ? totalSizeBytes / 1024 ** 3 : null,
    }
  }

  // Check if job has any VM with backup in progress
  const hasJobInProgress = (job: BackupJob): boolean => {
    const activeVmIds = (job.vms || [])
      .filter((v: BackupJobVM) => v.is_active)
      .map((v: BackupJobVM) => v.vm_id)
    return activeVmIds.some((vmId: string) => hasBackupInProgress(vmId, job.id))
  }

  const getJobInProgress = (job: BackupJob): BackupSnapshot | undefined => {
    const activeVmIds = (job.vms || [])
      .filter((v: BackupJobVM) => v.is_active)
      .map((v: BackupJobVM) => v.vm_id)
    for (const vmId of activeVmIds) {
      const snap = getBackupInProgress(vmId, job.id)
      if (snap) return snap
    }
    return undefined
  }

  // ========================================
  // Mutations
  // ========================================

  // Initiate restore
  const initiateRestoreMutation = useMutation({
    mutationFn: (data: InitiateRestoreRequest) => backupsApi.initiateRestore(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-restores'] })
      setShowRestoreModal(false)
      toast.success('Restauração iniciada com sucesso!')
    },
    onError: (error: any) => {
      toast.error(`Erro ao iniciar restauração: ${getErrorMessage(error)}`)
    },
  })

  // Delete snapshot
  const deleteSnapshotMutation = useMutation({
    mutationFn: (snapshotId: string) => backupsApi.deleteSnapshot(snapshotId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-snapshots'] })
      toast.success('Snapshot deletado com sucesso!')
    },
    onError: (error: any) => {
      toast.error(`Erro ao deletar snapshot: ${getErrorMessage(error)}`)
    },
  })

  // Verify snapshot
  const verifySnapshotMutation = useMutation({
    mutationFn: (snapshotId: string) => backupsApi.verifySnapshot(snapshotId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-snapshots'] })
      toast.success('Verificação de snapshot iniciada!')
    },
    onError: (error: any) => {
      toast.error(`Erro ao verificar snapshot: ${getErrorMessage(error)}`)
    },
  })

  // ========================================
  // Job Mutations
  // ========================================

  const createJobMutation = useMutation({
    mutationFn: (data: CreateBackupJobRequest) => backupsApi.createJob(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-jobs'] })
      setShowJobModal(false)
      toast.success('Job de backup criado com sucesso!')
    },
    onError: (error: any) => {
      toast.error(`Erro ao criar job: ${getErrorMessage(error)}`)
    },
  })

  const updateJobMutation = useMutation({
    mutationFn: ({ jobId, data }: { jobId: string; data: any }) =>
      backupsApi.updateJob(jobId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-jobs'] })
      setShowJobModal(false)
      setEditingJob(null)
      toast.success('Job atualizado com sucesso!')
    },
    onError: (error: any) => {
      toast.error(`Erro ao atualizar job: ${getErrorMessage(error)}`)
    },
  })

  const deleteJobMutation = useMutation({
    mutationFn: (jobId: string) => backupsApi.deleteJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-jobs'] })
      toast.success('Job de backup deletado com sucesso!')
    },
    onError: (error: any) => {
      toast.error(`Erro ao deletar job: ${getErrorMessage(error)}`)
    },
  })

  const executeJobMutation = useMutation({
    mutationFn: (jobId: string) => backupsApi.executeJob(jobId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-snapshots'] })
      queryClient.invalidateQueries({ queryKey: ['backup-jobs'] })
      toast.success('Job de backup iniciado com sucesso!')
    },
    onError: (error: any) => {
      toast.error(`Erro ao executar job: ${getErrorMessage(error)}`)
    },
  })

  const quickBackupMutation = useMutation({
    mutationFn: ({ jobId, vmId }: { jobId: string; vmId: string }) =>
      backupsApi.executeJob(jobId, vmId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-snapshots'] })
      queryClient.invalidateQueries({ queryKey: ['backup-jobs'] })
      toast.success('Quick backup iniciado!')
    },
    onError: (error: any) => {
      toast.error(`Erro ao iniciar quick backup: ${getErrorMessage(error)}`)
    },
  })

  const removeVMFromJobMutation = useMutation({
    mutationFn: ({ jobId, vmId }: { jobId: string; vmId: string }) =>
      backupsApi.removeVMFromJob(jobId, vmId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-jobs'] })
      toast.success('VM removida do job!')
    },
    onError: (error: any) => {
      toast.error(`Erro ao remover VM: ${getErrorMessage(error)}`)
    },
  })

  // Acknowledge alert
  const acknowledgeAlertMutation = useMutation({
    mutationFn: (alertId: string) => backupAlertsApi.acknowledgeAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-alerts'] })
      toast.success('Alerta reconhecido com sucesso!')
      setShowAlertDetailsModal(false)
      setSelectedAlert(null)
    },
    onError: (error: any) => {
      toast.error(`Erro ao reconhecer alerta: ${getErrorMessage(error)}`)
    },
  })

  // Resolve alert
  const resolveAlertMutation = useMutation({
    mutationFn: ({ alertId, notes }: { alertId: string; notes?: string }) =>
      backupAlertsApi.resolveAlert(alertId, notes),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-alerts'] })
      toast.success('Alerta resolvido com sucesso!')
      setShowResolveModal(false)
      setShowAlertDetailsModal(false)
      setSelectedAlert(null)
      setResolutionNotes('')
    },
    onError: (error: any) => {
      toast.error(`Erro ao resolver alerta: ${getErrorMessage(error)}`)
    },
  })

  // Dismiss alert
  const dismissAlertMutation = useMutation({
    mutationFn: (alertId: string) => backupAlertsApi.dismissAlert(alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['backup-alerts'] })
      toast.success('Alerta descartado com sucesso!')
      setShowAlertDetailsModal(false)
      setSelectedAlert(null)
    },
    onError: (error: any) => {
      toast.error(`Erro ao descartar alerta: ${getErrorMessage(error)}`)
    },
  })

  // ========================================
  // Auto Refresh
  // ========================================

  useEffect(() => {
    if (autoRefresh) {
      const interval = setInterval(() => {
        refetchSnapshots()
        refetchRestores()
        refetchJobs()
      }, 30_000) // 30 seconds
      return () => clearInterval(interval)
    }
  }, [autoRefresh, refetchSnapshots, refetchRestores, refetchJobs])

  // ========================================
  // Event Handlers
  // ========================================

  // ========================================
  // Job Event Handlers
  // ========================================

  const handleCreateJob = () => {
    createJobMutation.mutate(jobForm)
  }

  const handleEditJob = (job: BackupJob) => {
    const cronParts = job.cron_expression.split(' ')
    const [minute, hour, , , dayOfWeek] = cronParts
    let frequency: 'daily' | 'weekly' | 'monthly' = 'daily'
    if (dayOfWeek === '*' && cronParts[2] === '*') frequency = 'daily'
    else if (dayOfWeek !== '*' && cronParts[2] === '*') frequency = 'weekly'
    else if (cronParts[2] === '1' && dayOfWeek === '*') frequency = 'monthly'

    setJobFrequency(frequency)
    setJobTime(`${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`)
    setJobWeekDay(dayOfWeek === '*' ? '0' : dayOfWeek)

    // Detect retention preset
    const kl = job.keep_last ?? 0,
      kd = job.keep_daily ?? 0,
      kw = job.keep_weekly ?? 0,
      km = job.keep_monthly ?? 0
    let preset: typeof jobRetentionPreset = 'custom'
    if (kl === 7 && kd === 7 && kw === 0 && km === 0) preset = '7days'
    else if (kl === 15 && kd === 15 && kw === 2 && km === 0) preset = '15days'
    else if (kl === 30 && kd === 30 && kw === 4 && km === 1) preset = '30days'
    else if (kl === 60 && kd === 30 && kw === 8 && km === 2) preset = '60days'
    else if (kl === 90 && kd === 30 && kw === 12 && km === 3) preset = '90days'
    else setShowJobAdvancedRetention(true)

    setJobRetentionPreset(preset)
    setEditingJob(job)
    setJobForm({
      name: job.name,
      description: job.description || undefined,
      cron_expression: job.cron_expression,
      timezone: job.timezone || 'America/Sao_Paulo',
      backup_type: job.backup_type || 'snapshot',
      compression_level: job.compression_level || 'balanced',
      encryption_enabled: job.encryption_enabled ?? true,
      verification_enabled: job.verification_enabled ?? true,
      keep_last: job.keep_last ?? 7,
      keep_daily: job.keep_daily ?? 7,
      keep_weekly: job.keep_weekly ?? 4,
      keep_monthly: job.keep_monthly ?? 12,
      stop_on_failure: job.stop_on_failure ?? false,
      notify_on_failure: job.notify_on_failure ?? true,
      notification_emails: job.notification_emails || [],
      vm_ids: (job.vms || [])
        .filter((v: BackupJobVM) => v.is_active)
        .map((v: BackupJobVM) => v.vm_id),
    })
    setShowJobModal(true)
  }

  const handleUpdateJob = () => {
    if (!editingJob) return
    updateJobMutation.mutate({
      jobId: editingJob.id,
      data: {
        name: jobForm.name,
        description: jobForm.description,
        cron_expression: jobForm.cron_expression,
        timezone: jobForm.timezone,
        backup_type: jobForm.backup_type,
        compression_level: jobForm.compression_level,
        encryption_enabled: jobForm.encryption_enabled,
        verification_enabled: jobForm.verification_enabled,
        keep_last: jobForm.keep_last,
        keep_daily: jobForm.keep_daily,
        keep_weekly: jobForm.keep_weekly,
        keep_monthly: jobForm.keep_monthly,
        stop_on_failure: jobForm.stop_on_failure,
        notify_on_failure: jobForm.notify_on_failure,
        notification_emails: jobForm.notification_emails,
        vm_ids: jobForm.vm_ids,
      },
    })
  }

  const handleExecuteJob = (jobId: string) => {
    setExecutingJobId(jobId)
    executeJobMutation.mutate(jobId, {
      onSettled: () => setExecutingJobId(null),
    })
  }

  const handleDeleteJob = (jobId: string, jobName: string) => {
    setJobToDelete({ id: jobId, name: jobName })
  }

  const resetJobForm = () => {
    setJobForm({
      name: '',
      cron_expression: '0 2 * * *',
      timezone: 'America/Sao_Paulo',
      backup_type: 'snapshot',
      compression_level: 'balanced',
      encryption_enabled: true,
      verification_enabled: true,
      keep_last: 7,
      keep_daily: 7,
      keep_weekly: 4,
      keep_monthly: 12,
      stop_on_failure: false,
      notify_on_failure: true,
      notification_emails: [],
      vm_ids: [],
    })
    setJobFrequency('daily')
    setJobTime('02:00')
    setJobWeekDay('0')
    setJobRetentionPreset('30days')
    setShowJobAdvancedRetention(false)
    setEditingJob(null)
  }

  const handleInitiateRestore = () => {
    setShowRestoreConfirm(true)
  }

  const confirmRestore = () => {
    // Preparar payload baseado no tipo de restauração
    const payload: any = {
      snapshot_id: restoreForm.snapshot_id,
      restore_type: restoreForm.restore_type,
      start_after_restore: restoreForm.start_after_restore,
      restore_network_config: restoreForm.restore_network_config,
    }

    // Adicionar campos específicos por tipo
    if (restoreForm.restore_type === 'full') {
      payload.target_vm_id = restoreForm.target_vm_id
      payload.replace_existing = restoreForm.replace_existing
    } else if (restoreForm.restore_type === 'clone') {
      payload.new_vm_name = restoreForm.new_vm_name
    }

    initiateRestoreMutation.mutate(payload)
    setShowRestoreConfirm(false)
  }

  const handleDeleteSnapshot = (snapshotId: string) => {
    setSnapshotToDelete(snapshotId)
  }

  const handleVerifySnapshot = (snapshotId: string) => {
    verifySnapshotMutation.mutate(snapshotId)
  }

  // ========================================
  // Helper Functions
  // ========================================

  const tabs: { id: TabKey; label: string; icon: typeof BarChart3Icon }[] = [
    { id: 'usage', label: 'Uso & Custos', icon: BarChart3Icon },
    { id: 'snapshots', label: 'Pontos de Restauração', icon: HardDriveIcon },
    { id: 'jobs', label: 'Jobs de Backup', icon: ZapIcon },
    { id: 'logs', label: 'Logs', icon: FileArchiveIcon },
    { id: 'alerts', label: 'Alertas', icon: AlertTriangleIcon },
  ]

  const getStatusBadge = (status: string) => {
    const label = getBackupStatusLabel(status)

    const dotColorMap: Record<string, string> = {
      completed: 'bg-emerald-500',
      verified: 'bg-teal-500',
      running: 'bg-blue-500 animate-pulse',
      pending: 'bg-amber-400 animate-pulse',
      failed: 'bg-rose-500',
      active: 'bg-emerald-500',
      paused: 'bg-slate-400',
      disabled: 'bg-slate-300',
    }

    const bgColorMap: Record<string, string> = {
      completed: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
      verified: 'bg-teal-50 text-teal-700 ring-teal-600/20',
      running: 'bg-blue-50 text-blue-700 ring-blue-600/20',
      pending: 'bg-amber-50 text-amber-700 ring-amber-600/20',
      failed: 'bg-rose-50 text-rose-700 ring-rose-600/20',
      active: 'bg-emerald-50 text-emerald-700 ring-emerald-600/20',
      paused: 'bg-slate-50 text-slate-600 ring-slate-500/20',
      disabled: 'bg-slate-50 text-slate-500 ring-slate-400/20',
    }

    return (
      <span
        className={`inline-flex items-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium ring-1 ring-inset ${bgColorMap[status] || 'bg-gray-50 text-gray-600 ring-gray-500/20'}`}
      >
        <span className={`size-1.5 rounded-full ${dotColorMap[status] || 'bg-gray-400'}`} />
        {label}
      </span>
    )
  }

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Nunca'
    return new Date(dateString).toLocaleString('pt-BR')
  }

  const formatRelativeTime = (dateString?: string) => {
    if (!dateString) return null
    const now = new Date()
    const date = new Date(dateString)
    const diffMs = date.getTime() - now.getTime()
    const absDiffMs = Math.abs(diffMs)
    const minutes = Math.floor(absDiffMs / 60_000)
    const hours = Math.floor(minutes / 60)
    const days = Math.floor(hours / 24)

    if (diffMs > 0) {
      // future
      if (minutes < 60) return `em ${minutes}min`
      if (hours < 24)
        return `em ${hours}h${minutes % 60 > 0 ? `${(minutes % 60).toString().padStart(2, '0')}` : ''}`
      return `em ${days}d`
    }
    // past
    if (minutes < 60) return `há ${minutes}min`
    if (hours < 24) return `há ${hours}h`
    return `há ${days}d`
  }

  const formatCronDescription = (cron: string) => {
    const parts = cron.split(' ')
    if (parts.length < 5) return cron
    const [minute, hour, dayOfMonth, , dayOfWeek] = parts
    const time = `${hour.padStart(2, '0')}:${minute.padStart(2, '0')}`
    if (dayOfWeek !== '*' && dayOfMonth === '*') {
      const days: Record<string, string> = {
        '0': 'Dom',
        '1': 'Seg',
        '2': 'Ter',
        '3': 'Qua',
        '4': 'Qui',
        '5': 'Sex',
        '6': 'Sab',
      }
      return `Semanal (${days[dayOfWeek] || dayOfWeek}) às ${time}`
    }
    if (dayOfMonth !== '*') return `Mensal (dia ${dayOfMonth}) às ${time}`
    return `Diário às ${time}`
  }

  // Filtered data
  const filteredSnapshots = snapshots.filter((snapshot) => {
    const matchesSearch =
      searchTerm === '' ||
      snapshot.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      snapshot.description?.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesSearch
  })

  // ========================================
  // Tab Renderers
  // ========================================

  const renderSnapshotsTab = () => (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
        <div className="flex-1">
          <div className="relative">
            <SearchIcon className="absolute top-1/2 left-3 size-4 -translate-y-1/2 transform text-gray-400" />
            <input
              type="text"
              placeholder="Buscar backups..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white py-2 pr-4 pl-9 text-sm placeholder:text-gray-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500 sm:w-auto"
        >
          <option value="">Todos os status</option>
          <option value={FILTRO_DE_BACKUP_CONCLUIDO}>Concluído</option>
          <option value="running">Em execução</option>
          <option value="failed">Falhou</option>
        </select>
        <button
          onClick={() => refetchSnapshots()}
          className="inline-flex w-full items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50 sm:w-auto"
        >
          <RefreshCwIcon className="size-3.5" />
          <span>Atualizar</span>
        </button>
      </div>

      {/* Loading State */}
      {snapshotsLoading && (
        <div className="flex flex-col items-center justify-center py-16">
          <LoaderIcon className="size-7 animate-spin text-purple-600" />
          <p className="mt-3 text-sm text-gray-500">Carregando backups...</p>
        </div>
      )}

      {/* Empty State */}
      {!snapshotsLoading && filteredSnapshots.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white py-16">
          <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-gray-100">
            <HardDriveIcon className="size-7 text-gray-400" />
          </div>
          <h3 className="text-base font-semibold text-gray-900">Nenhum backup encontrado</h3>
          <p className="mt-1 max-w-xs text-center text-sm text-gray-500">
            {searchTerm || statusFilter
              ? 'Tente ajustar os filtros.'
              : 'Crie um agendamento para começar.'}
          </p>
        </div>
      )}

      {/* Snapshots List - Grouped by VM */}
      {!snapshotsLoading && filteredSnapshots.length > 0 && (
        <div className="space-y-3">
          {groupSnapshotsByVM(filteredSnapshots).map((vmGroup) => {
            const isExpanded = expandedVMs.has(vmGroup.vm_id)
            const totalBackups = vmGroup.snapshots.length
            const vmTotalSizeBytes = vmGroup.snapshots.reduce(
              (acc, s) => acc + (s.size_bytes ?? 0),
              0,
            )
            const vmStorageGb = vmTotalSizeBytes > 0 ? vmTotalSizeBytes / 1024 ** 3 : null
            const completedCount = vmGroup.snapshots.filter((s) =>
              isBackupConcluido(s.status),
            ).length

            return (
              <div
                key={vmGroup.vm_id}
                className="overflow-hidden rounded-xl border border-gray-200 bg-white"
              >
                {/* VM Header */}
                <div
                  className="flex cursor-pointer items-center justify-between px-5 py-4 transition-colors hover:bg-gray-50/50"
                  onClick={() => toggleVMExpand(vmGroup.vm_id)}
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <div className="flex size-9 shrink-0 items-center justify-center rounded-lg bg-purple-50">
                      <ServerIcon className="size-4.5 text-purple-600" />
                    </div>
                    <div className="min-w-0">
                      <h3 className="truncate text-sm font-semibold text-gray-900">
                        {vmGroup.vm_name}
                      </h3>
                      <div className="mt-0.5 flex items-center gap-3">
                        <span className="text-xs text-gray-500">
                          {totalBackups} backup{totalBackups === 1 ? '' : 's'}
                        </span>
                        <span className="text-xs text-gray-300">|</span>
                        <span className="text-xs text-gray-500">
                          {vmStorageGb == null ? '-' : `${formatGB(vmStorageGb)} GB`}
                        </span>
                        <span className="text-xs text-gray-300">|</span>
                        <span className="text-xs font-medium text-emerald-600">
                          {completedCount} ok
                        </span>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`flex size-7 items-center justify-center rounded-md transition-colors ${isExpanded ? 'bg-gray-100' : 'hover:bg-gray-100'}`}
                  >
                    {isExpanded ? (
                      <ChevronUpIcon className="size-4 text-gray-500" />
                    ) : (
                      <ChevronDownIcon className="size-4 text-gray-500" />
                    )}
                  </div>
                </div>

                {/* Backups List */}
                {isExpanded && (
                  <div className="border-t border-gray-100">
                    <div className="divide-y divide-gray-100">
                      {vmGroup.snapshots.map((snapshot) => (
                        <div
                          key={snapshot.id}
                          className="px-5 py-4 transition-colors hover:bg-gray-50/50"
                        >
                          {/* Snapshot Row */}
                          <div className="mb-2 flex items-start justify-between gap-4">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <h4 className="truncate text-sm font-medium text-gray-900">
                                  {snapshot.name}
                                </h4>
                                {snapshot.is_verified && (
                                  <CheckCircleIcon className="size-3.5 shrink-0 text-teal-500" />
                                )}
                              </div>
                              {snapshot.description && (
                                <p className="mt-0.5 line-clamp-1 text-xs text-gray-500">
                                  {snapshot.description}
                                </p>
                              )}
                            </div>
                            {getStatusBadge(snapshot.status)}
                          </div>

                          {/* Metadata */}
                          <div className="mb-3 flex items-center gap-4 text-xs text-gray-500">
                            <span className="inline-flex items-center gap-1">
                              <ClockIcon className="size-3" />
                              {formatDate(snapshot.backup_time)}
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <HardDriveIcon className="size-3" />
                              {snapshot.size_bytes
                                ? formatBytes(snapshot.size_bytes)
                                : snapshot.vm_storage_gb == null
                                  ? '-'
                                  : `${formatGB(snapshot.vm_storage_gb)} GB`}
                            </span>
                          </div>

                          {/* Progress */}
                          {(snapshot.status === 'running' || snapshot.status === 'pending') && (
                            <div className="mb-3 rounded-lg border border-blue-100 bg-blue-50/70 p-3">
                              <div className="mb-2 flex items-center justify-between text-xs">
                                <div className="flex items-center gap-1.5">
                                  <LoaderIcon className="size-3.5 animate-spin text-blue-600" />
                                  <span className="font-medium text-blue-700">
                                    {snapshot.status === 'pending'
                                      ? 'Aguardando...'
                                      : 'Em progresso'}
                                  </span>
                                </div>
                                <span className="font-semibold text-blue-700 tabular-nums">
                                  {snapshot.progress_percentage}%
                                </span>
                              </div>
                              <div className="h-1.5 w-full overflow-hidden rounded-full bg-blue-200/60">
                                <div
                                  className="h-1.5 rounded-full bg-blue-500 transition-all duration-500 ease-out"
                                  style={{ width: `${snapshot.progress_percentage}%` }}
                                />
                              </div>
                            </div>
                          )}

                          {/* Restore Progress */}
                          {(() => {
                            const activeRestore = getRestoreInProgress(snapshot.id)
                            if (!activeRestore) return null
                            return (
                              <div className="mb-3 rounded-lg border border-emerald-100 bg-emerald-50/70 p-3">
                                <div className="mb-2 flex items-center justify-between text-xs">
                                  <div className="flex items-center gap-1.5">
                                    <RotateCcwIcon className="size-3.5 animate-spin text-emerald-600" />
                                    <span className="font-medium text-emerald-700">
                                      {activeRestore.status === 'pending'
                                        ? 'Aguardando restauração...'
                                        : 'Restauração em andamento'}
                                    </span>
                                  </div>
                                  <span className="font-semibold text-emerald-700 tabular-nums">
                                    {activeRestore.progress_percentage}%
                                  </span>
                                </div>
                                <div className="h-1.5 w-full overflow-hidden rounded-full bg-emerald-200/60">
                                  <div
                                    className="h-1.5 rounded-full bg-emerald-500 transition-all duration-500 ease-out"
                                    style={{ width: `${activeRestore.progress_percentage}%` }}
                                  />
                                </div>
                              </div>
                            )
                          })()}

                          {/* Error */}
                          {snapshot.error_message && (
                            <div className="mb-3 flex items-start gap-2 rounded-lg border border-rose-100 bg-rose-50 p-2.5">
                              <AlertCircleIcon className="mt-0.5 size-3.5 shrink-0 text-rose-500" />
                              <p className="text-xs text-rose-700">{snapshot.error_message}</p>
                            </div>
                          )}

                          {/* Actions */}
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => {
                                setSelectedSnapshot(snapshot)
                                setShowDetailsModal(true)
                              }}
                              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs text-gray-600 transition-colors hover:bg-gray-100"
                            >
                              <EyeIcon className="size-3" />
                              <span>Detalhes</span>
                            </button>
                            <button
                              onClick={() => {
                                setRestoreForm((prev) => ({
                                  ...prev,
                                  snapshot_id: snapshot.id,
                                  target_vm_id: snapshot.vm_id,
                                }))
                                setSelectedSnapshot(snapshot)
                                setShowRestoreModal(true)
                              }}
                              disabled={!isBackupConcluido(snapshot.status)}
                              className="inline-flex items-center gap-1 rounded-md bg-purple-600 px-2.5 py-1.5 text-xs font-medium text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
                              title={
                                snapshot.status === 'verified'
                                  ? 'Pronto para restaurar'
                                  : snapshot.status === 'verifying'
                                    ? 'Verificação em andamento — já dá para restaurar'
                                    : isBackupConcluido(snapshot.status)
                                      ? 'Backup concluído (ainda não verificado)'
                                      : 'O backup precisa terminar antes de restaurar'
                              }
                            >
                              <RotateCcwIcon className="size-3" />
                              <span>Restaurar</span>
                            </button>
                            <button
                              onClick={() => handleVerifySnapshot(snapshot.id)}
                              disabled={
                                snapshot.status !== 'completed' || verifySnapshotMutation.isPending
                              }
                              className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs text-blue-600 transition-colors hover:bg-blue-50 disabled:cursor-not-allowed disabled:text-gray-400"
                              title={
                                snapshot.status === 'verified'
                                  ? 'Já verificado'
                                  : snapshot.status === 'verifying'
                                    ? 'Verificação em andamento'
                                    : snapshot.status === 'completed'
                                      ? 'Verificar integridade'
                                      : 'Deve estar concluído para verificar'
                              }
                            >
                              <CheckCircleIcon className="size-3" />
                              <span>Verificar</span>
                            </button>
                            <div className="flex-1" />
                            <button
                              onClick={() => handleDeleteSnapshot(snapshot.id)}
                              disabled={deleteSnapshotMutation.isPending}
                              className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-rose-500 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:text-gray-400"
                              title="Deletar backup"
                            >
                              <Trash2Icon className="size-3" />
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )

  // ========================================
  // Jobs Tab
  // ========================================

  const filteredJobs = backupJobs.filter((job) => {
    if (!jobSearchTerm) return true
    const term = jobSearchTerm.toLowerCase()
    if (job.name.toLowerCase().includes(term)) return true
    if (job.description?.toLowerCase().includes(term)) return true
    const vmNames = (job.vms || []).map((v) => v.vm_name || '').join(' ')
    if (vmNames.toLowerCase().includes(term)) return true
    return false
  })

  const renderJobsTab = () => (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col items-stretch gap-3 sm:flex-row sm:items-center">
        <div className="flex-1">
          <div className="relative">
            <SearchIcon className="absolute top-1/2 left-3 size-4 -translate-y-1/2 transform text-gray-400" />
            <input
              type="text"
              placeholder="Buscar por job ou VM..."
              value={jobSearchTerm}
              onChange={(e) => setJobSearchTerm(e.target.value)}
              className="w-full rounded-lg border border-gray-200 bg-white py-2 pr-4 pl-9 text-sm placeholder:text-gray-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
            />
          </div>
        </div>
        <button
          onClick={() => {
            resetJobForm()
            setShowJobModal(true)
          }}
          className="flex items-center justify-center gap-2 rounded-lg bg-purple-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-purple-700"
        >
          <PlusIcon className="size-4" />
          <span>Novo Job</span>
        </button>
      </div>

      {/* Loading State */}
      {jobsLoading && (
        <div className="flex flex-col items-center justify-center py-16">
          <LoaderIcon className="size-7 animate-spin text-purple-600" />
          <p className="mt-3 text-sm text-gray-500">Carregando jobs...</p>
        </div>
      )}

      {/* Empty State */}
      {!jobsLoading && filteredJobs.length === 0 && (
        <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white py-16">
          <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-gray-100">
            <ZapIcon className="size-7 text-gray-400" />
          </div>
          <h3 className="text-base font-semibold text-gray-900">Nenhum job de backup</h3>
          <p className="mt-1 max-w-xs text-center text-sm text-gray-500">
            {jobSearchTerm
              ? 'Tente ajustar os filtros.'
              : 'Crie um job para agendar backups automáticos de suas VMs.'}
          </p>
          {!jobSearchTerm && (
            <button
              onClick={() => {
                resetJobForm()
                setShowJobModal(true)
              }}
              className="mt-5 rounded-lg bg-purple-600 px-5 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-purple-700"
            >
              Criar Job de Backup
            </button>
          )}
        </div>
      )}

      {/* Jobs List */}
      {!jobsLoading && filteredJobs.length > 0 && (
        <div className="space-y-3">
          {filteredJobs.map((job) => {
            const isExpanded = expandedJobs.has(job.id)
            const activeVMs = (job.vms || []).filter((v) => v.is_active)
            const vmsRemovidas = activeVMs.filter((v) => isVMRemovida(v))
            const inProgress = hasJobInProgress(job)
            const activeSnapshot = getJobInProgress(job)
            const aggregates = getJobAggregates(job.id)
            const isRunning = job.last_run_status === 'running'

            return (
              <div
                key={job.id}
                className={`rounded-xl border bg-white transition-all ${
                  inProgress || isRunning
                    ? 'border-blue-200 ring-1 ring-blue-100'
                    : 'border-gray-200'
                }`}
              >
                {/* Job Header */}
                <div className="px-5 py-4">
                  <div className="flex items-center justify-between gap-3">
                    <div
                      className="flex min-w-0 flex-1 cursor-pointer items-center gap-3"
                      onClick={() => toggleJobExpand(job.id)}
                    >
                      <div
                        className={`flex size-9 shrink-0 items-center justify-center rounded-lg ${
                          job.status === 'active'
                            ? 'bg-emerald-50'
                            : job.status === 'paused'
                              ? 'bg-amber-50'
                              : 'bg-gray-100'
                        }`}
                      >
                        <ZapIcon
                          className={`size-4.5 ${
                            job.status === 'active'
                              ? 'text-emerald-600'
                              : job.status === 'paused'
                                ? 'text-amber-500'
                                : 'text-gray-400'
                          }`}
                        />
                      </div>
                      <div className="min-w-0">
                        <div className="flex items-center gap-2">
                          <h3 className="truncate text-sm font-semibold text-gray-900">
                            {job.name}
                          </h3>
                          {getStatusBadge(job.status)}
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-3">
                          <span className="text-xs text-gray-500">
                            {activeVMs.length} VM{activeVMs.length === 1 ? '' : 's'}
                            {vmsRemovidas.length > 0 && (
                              <span
                                className="font-medium text-rose-500"
                                title={`${vmsRemovidas.map((v) => v.vm_name).join(', ')}`}
                              >
                                {' '}
                                ({vmsRemovidas.length} removida
                                {vmsRemovidas.length === 1 ? '' : 's'})
                              </span>
                            )}
                          </span>
                          <span className="text-xs text-gray-300">|</span>
                          <span className="text-xs text-gray-500">
                            {formatCronDescription(job.cron_expression)}
                          </span>
                          <span className="text-xs text-gray-300">|</span>
                          <span className="text-xs text-gray-500">
                            {aggregates.restorePointCount} ponto
                            {aggregates.restorePointCount === 1 ? '' : 's'} de restauração
                          </span>
                          {aggregates.totalSizeGB != null && (
                            <>
                              <span className="text-xs text-gray-300">|</span>
                              <span className="text-xs text-gray-500">
                                {formatGB(aggregates.totalSizeGB)} GB
                              </span>
                            </>
                          )}
                          {job.last_run_at && (
                            <>
                              <span className="text-xs text-gray-300">|</span>
                              <span
                                className={`text-xs ${
                                  job.last_run_status === 'failed'
                                    ? 'text-rose-500'
                                    : job.last_run_status === 'partial'
                                      ? 'text-amber-500'
                                      : 'text-gray-500'
                                }`}
                                title={`Última execução: ${formatDate(job.last_run_at)}`}
                              >
                                Última: {formatRelativeTime(job.last_run_at)}
                                {job.last_run_status === 'success' && ' ✓'}
                                {job.last_run_status === 'partial' && ' ⚠'}
                                {job.last_run_status === 'failed' && ' ✗'}
                              </span>
                            </>
                          )}
                          {job.next_run_at &&
                            job.status === 'active' &&
                            (() => {
                              const isOverdue = new Date(job.next_run_at) < new Date()
                              return (
                                <>
                                  <span className="text-xs text-gray-300">|</span>
                                  <span
                                    className={`text-xs ${isOverdue ? 'text-amber-500' : 'text-emerald-600'}`}
                                    title={`Próxima execução: ${formatDate(job.next_run_at)}`}
                                  >
                                    Próxima:{' '}
                                    {isOverdue
                                      ? `atrasada (${formatRelativeTime(job.next_run_at)})`
                                      : formatRelativeTime(job.next_run_at)}
                                  </span>
                                </>
                              )
                            })()}
                        </div>
                      </div>
                    </div>

                    <div className="flex shrink-0 items-center gap-2">
                      {/* Execute Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleExecuteJob(job.id)
                        }}
                        disabled={
                          inProgress ||
                          isRunning ||
                          executingJobId === job.id ||
                          activeVMs.length === 0
                        }
                        className="inline-flex items-center gap-1.5 rounded-lg bg-purple-600 px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
                        title={
                          activeVMs.length === 0
                            ? 'Nenhuma VM no job'
                            : inProgress || isRunning
                              ? 'Backup em execução'
                              : 'Executar job agora'
                        }
                      >
                        {executingJobId === job.id ? (
                          <LoaderIcon className="size-3.5 animate-spin" />
                        ) : (
                          <PlayIcon className="size-3.5" />
                        )}
                        <span>Executar Agora</span>
                      </button>

                      {/* Expand/Collapse */}
                      <button
                        onClick={() => toggleJobExpand(job.id)}
                        className={`flex size-7 items-center justify-center rounded-md transition-colors ${isExpanded ? 'bg-gray-100' : 'hover:bg-gray-100'}`}
                      >
                        {isExpanded ? (
                          <ChevronUpIcon className="size-4 text-gray-500" />
                        ) : (
                          <ChevronDownIcon className="size-4 text-gray-500" />
                        )}
                      </button>
                    </div>
                  </div>

                  {/* In-Progress Banner */}
                  {(inProgress || isRunning) && activeSnapshot && (
                    <div className="mt-3 rounded-lg border border-blue-100 bg-blue-50 px-3 py-2.5">
                      <div className="mb-2 flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <LoaderIcon className="size-3.5 shrink-0 animate-spin text-blue-600" />
                          <span className="text-xs font-medium text-blue-700">
                            {activeSnapshot.status === 'pending'
                              ? 'Aguardando início...'
                              : `Backup em execução — ${activeSnapshot.vm_name || 'VM'}`}
                          </span>
                        </div>
                        <span className="text-xs font-semibold text-blue-700 tabular-nums">
                          {activeSnapshot.progress_percentage}%
                        </span>
                      </div>
                      <div className="h-1.5 overflow-hidden rounded-full bg-blue-100">
                        <div
                          className="h-full rounded-full bg-blue-500 transition-all duration-500"
                          style={{ width: `${activeSnapshot.progress_percentage}%` }}
                        />
                      </div>
                    </div>
                  )}

                  {/* Last run result per VM */}
                  {job.last_run_at &&
                    !inProgress &&
                    !isRunning &&
                    (() => {
                      const lastRunSnaps = snapshots.filter(
                        (s) =>
                          s.job_id === job.id &&
                          job.last_run_at &&
                          Math.abs(
                            new Date(s.created_at).getTime() - new Date(job.last_run_at).getTime(),
                          ) < 60_000,
                      )
                      if (lastRunSnaps.length === 0) return null
                      return (
                        <div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs">
                          {lastRunSnaps.map((s) => (
                            <span
                              key={s.id}
                              className={`inline-flex items-center gap-1 ${s.status === 'failed' ? 'text-rose-500' : isBackupConcluido(s.status) ? 'text-emerald-600' : 'text-gray-400'}`}
                            >
                              {s.status === 'failed' ? (
                                <XCircleIcon className="size-3" />
                              ) : isBackupConcluido(s.status) ? (
                                <CheckCircleIcon className="size-3" />
                              ) : (
                                <ClockIcon className="size-3" />
                              )}
                              {s.vm_name || 'VM'}
                            </span>
                          ))}
                          {job.consecutive_failures > 1 && (
                            <span className="font-medium text-rose-500">
                              ({job.consecutive_failures} falhas consecutivas)
                            </span>
                          )}
                        </div>
                      )
                    })()}
                </div>

                {/* Expanded Content */}
                {isExpanded && (
                  <div className="border-t border-gray-100">
                    {/* Job Config Summary */}
                    <div className="bg-gray-50/50 px-5 py-3">
                      <div className="grid grid-cols-2 gap-2">
                        <div className="rounded-md border border-gray-100 bg-white px-2.5 py-1.5">
                          <p className="text-[10px] font-medium tracking-wider text-gray-400 uppercase">
                            Retenção
                          </p>
                          <p className="text-xs text-gray-700">
                            {job.keep_last} último{job.keep_last === 1 ? '' : 's'}
                            {(job.keep_daily > 0 ||
                              job.keep_weekly > 0 ||
                              job.keep_monthly > 0) && (
                              <span className="text-gray-400">
                                {' '}
                                · {job.keep_daily}d / {job.keep_weekly}s / {job.keep_monthly}m
                              </span>
                            )}
                          </p>
                        </div>
                        <div className="rounded-md border border-gray-100 bg-white px-2.5 py-1.5">
                          <p className="text-[10px] font-medium tracking-wider text-gray-400 uppercase">
                            Ao falhar
                          </p>
                          <p className="text-xs text-gray-700">
                            {job.stop_on_failure ? 'Parar execução' : 'Continuar próxima VM'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* VMs List */}
                    <div className="divide-y divide-gray-100">
                      {activeVMs.length === 0 && (
                        <div className="px-5 py-6 text-center">
                          <p className="text-sm text-gray-500">Nenhuma VM neste job.</p>
                        </div>
                      )}
                      {activeVMs
                        .sort((a, b) => a.priority - b.priority)
                        .map((jobVM, idx) => {
                          const vmInProgress = hasBackupInProgress(jobVM.vm_id, job.id)
                          const vmSnapshot = getBackupInProgress(jobVM.vm_id, job.id)

                          return (
                            <div
                              key={jobVM.id}
                              className="px-5 py-3 transition-colors hover:bg-gray-50/50"
                            >
                              <div className="flex items-center gap-3">
                                <div className="flex min-w-0 flex-1 items-center gap-3">
                                  <span className="w-5 shrink-0 text-center font-mono text-xs text-gray-400">
                                    #{idx + 1}
                                  </span>
                                  <ServerIcon
                                    className={`size-3.5 shrink-0 ${
                                      vmInProgress
                                        ? 'animate-pulse text-blue-500'
                                        : isVMRemovida(jobVM)
                                          ? 'text-rose-300'
                                          : 'text-gray-400'
                                    }`}
                                  />
                                  <span
                                    className={`truncate text-sm ${isVMRemovida(jobVM) ? 'text-gray-400 line-through' : 'text-gray-900'}`}
                                  >
                                    {jobVM.vm_name || `VM ${jobVM.vm_id.slice(0, 8)}`}
                                  </span>
                                  {/* `|| isVMRemovida` porque a VM apagada do banco chega
                                      sem `vm_status`: sem isto a linha sairia riscada e
                                      sem dizer por quê. */}
                                  {(jobVM.vm_status || isVMRemovida(jobVM)) && (
                                    <span
                                      className={`shrink-0 rounded px-1.5 py-0.5 text-xs ${
                                        isVMRemovida(jobVM)
                                          ? 'bg-rose-50 font-medium text-rose-600'
                                          : jobVM.vm_status === 'running'
                                            ? 'bg-emerald-50 text-emerald-700'
                                            : jobVM.vm_status === 'stopped'
                                              ? 'bg-gray-100 text-gray-600'
                                              : 'bg-gray-100 text-gray-500'
                                      }`}
                                    >
                                      {isVMRemovida(jobVM) ? 'removida' : jobVM.vm_status}
                                    </span>
                                  )}
                                  {vmInProgress && vmSnapshot && (
                                    <span className="text-xs font-medium text-blue-600 tabular-nums">
                                      {vmSnapshot.progress_percentage}%
                                    </span>
                                  )}
                                </div>
                                <span
                                  className="w-24 shrink-0 text-right text-xs text-gray-500 tabular-nums"
                                  title="Pontos de restauração"
                                >
                                  {jobVM.snapshot_count ?? 0}{' '}
                                  {jobVM.snapshot_count === 1 ? 'ponto' : 'pontos'}
                                </span>
                                <span
                                  className="w-24 shrink-0 text-right text-xs text-gray-500 tabular-nums"
                                  title="Storage consumido"
                                >
                                  {formatBytes(jobVM.storage_used_bytes ?? 0)}
                                </span>
                                <div className="flex shrink-0 items-center gap-0.5">
                                  <button
                                    onClick={() =>
                                      quickBackupMutation.mutate({
                                        jobId: job.id,
                                        vmId: jobVM.vm_id,
                                      })
                                    }
                                    disabled={
                                      vmInProgress ||
                                      hasBackupInProgress(jobVM.vm_id) ||
                                      quickBackupMutation.isPending ||
                                      isVMRemovida(jobVM)
                                    }
                                    className="p-1 text-xs text-gray-400 transition-colors hover:text-violet-600 disabled:cursor-not-allowed disabled:opacity-30"
                                    title={
                                      vmInProgress
                                        ? 'Backup em execução neste job'
                                        : hasBackupInProgress(jobVM.vm_id)
                                          ? 'VM já possui backup em execução em outro job'
                                          : isVMRemovida(jobVM)
                                            ? 'VM removida'
                                            : 'Quick backup'
                                    }
                                  >
                                    <PlayIcon className="size-3.5" />
                                  </button>
                                  <button
                                    onClick={() =>
                                      removeVMFromJobMutation.mutate({
                                        jobId: job.id,
                                        vmId: jobVM.vm_id,
                                      })
                                    }
                                    disabled={removeVMFromJobMutation.isPending}
                                    className="p-1 text-xs text-gray-400 transition-colors hover:text-rose-500"
                                    title="Remover VM do job"
                                  >
                                    <XIcon className="size-3.5" />
                                  </button>
                                </div>
                              </div>
                            </div>
                          )
                        })}
                    </div>

                    {/* VMs Removidas (aguardando prune) */}
                    {(() => {
                      const removedVMs = (job.vms || []).filter((v) => !v.is_active)
                      if (removedVMs.length === 0) return null
                      return (
                        <div className="border-t border-gray-100">
                          <button
                            onClick={(e) => {
                              e.stopPropagation()
                              toggleRemovedVMs(job.id)
                            }}
                            className="flex w-full items-center gap-2 px-5 py-2 text-xs text-gray-400 transition-colors hover:bg-gray-50"
                          >
                            <ArchiveIcon className="size-3" />
                            <span>
                              {removedVMs.length} VM{removedVMs.length === 1 ? '' : 's'} removida
                              {removedVMs.length === 1 ? '' : 's'} (aguardando prune)
                            </span>
                            <ChevronDownIcon
                              className={`ml-auto size-3 transition-transform ${expandedRemovedVMs.has(job.id) ? 'rotate-180' : ''}`}
                            />
                          </button>
                          {expandedRemovedVMs.has(job.id) &&
                            removedVMs.map((jobVM) => (
                              <div key={jobVM.id} className="bg-gray-50/50 px-5 py-2">
                                <div className="flex items-center gap-3">
                                  <ServerIcon className="size-3.5 text-gray-300" />
                                  <span className="truncate text-sm text-gray-400 line-through">
                                    {jobVM.vm_name || `VM ${jobVM.vm_id.slice(0, 8)}`}
                                  </span>
                                  {jobVM.removed_at && (
                                    <span className="ml-auto shrink-0 text-xs text-gray-400">
                                      Removida em {formatDate(jobVM.removed_at)}
                                    </span>
                                  )}
                                </div>
                              </div>
                            ))}
                        </div>
                      )
                    })()}

                    {/* Job Actions */}
                    <div className="flex items-center gap-1.5 border-t border-gray-100 bg-gray-50/30 px-5 py-3">
                      <button
                        onClick={() => handleEditJob(job)}
                        className="inline-flex items-center gap-1 rounded-md px-2.5 py-1.5 text-xs text-gray-600 transition-colors hover:bg-gray-100"
                      >
                        <Edit3Icon className="size-3" />
                        <span>Editar</span>
                      </button>
                      <div className="flex-1" />
                      <button
                        onClick={() => handleDeleteJob(job.id, job.name)}
                        disabled={deleteJobMutation.isPending}
                        className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-rose-500 transition-colors hover:bg-rose-50 disabled:cursor-not-allowed disabled:text-gray-400"
                      >
                        <Trash2Icon className="size-3" />
                        <span>Deletar</span>
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )
          })}
        </div>
      )}
    </div>
  )

  const renderLogsTab = () => {
    const getEventIcon = (eventType: string) => {
      switch (eventType) {
        case 'backup_started':
        case 'backup_completed':
        case 'backup_failed':
          return { icon: DownloadIcon, bg: 'bg-blue-50', color: 'text-blue-600' }
        case 'backup_verified':
        case 'verification_failed':
          return { icon: CheckCircleIcon, bg: 'bg-indigo-50', color: 'text-indigo-600' }
        case 'snapshot_deleted':
          return { icon: Trash2Icon, bg: 'bg-gray-100', color: 'text-gray-500' }
        case 'restore_started':
        case 'restore_completed':
        case 'restore_failed':
          return { icon: RotateCcwIcon, bg: 'bg-emerald-50', color: 'text-emerald-600' }
        default:
          return { icon: FileArchiveIcon, bg: 'bg-gray-100', color: 'text-gray-500' }
      }
    }

    const getEventLabel = (eventType: string) => {
      const labels: Record<string, string> = {
        backup_started: 'Backup em andamento',
        backup_completed: 'Backup',
        backup_failed: 'Backup falhou',
        backup_verified: 'Backup verificado',
        verification_failed: 'Verificação falhou',
        snapshot_deleted: 'Ponto de restauração removido',
        restore_started: 'Restauração em andamento',
        restore_completed: 'Restauração',
        restore_failed: 'Restauração falhou',
      }
      return labels[eventType] || eventType
    }

    const getStatusBadgeForLog = (logStatus: string) => {
      switch (logStatus) {
        case 'success':
          return (
            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
              <CheckCircleIcon className="size-3" />
              Sucesso
            </span>
          )
        case 'failure':
          return (
            <span className="inline-flex items-center gap-1 rounded-full bg-rose-50 px-2 py-0.5 text-[11px] font-medium text-rose-700">
              <XCircleIcon className="size-3" />
              Falha
            </span>
          )
        case 'pending':
          return (
            <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[11px] font-medium text-amber-700">
              <ClockIcon className="size-3" />
              Pendente
            </span>
          )
        default:
          return (
            <span className="inline-flex items-center rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
              {logStatus}
            </span>
          )
      }
    }

    // Determine if an event is backup-type or restore-type
    const isBackupEvent = (eventType: string) =>
      eventType.startsWith('backup_') ||
      eventType === 'verification_failed' ||
      eventType === 'snapshot_deleted'

    // Merge started + completed/failed into single entries (backup and restore)
    const mergePairedLogs = (logs: typeof activityLogs) => {
      const pairs: [string, string, string][] = [
        ['backup_started', 'backup_completed', 'backup_failed'],
        ['restore_started', 'restore_completed', 'restore_failed'],
      ]

      // Key: use snapshot_id if available, fallback to snapshot_name
      const logKey = (prefix: string, log: (typeof activityLogs)[0]) =>
        `${prefix}:${log.snapshot_id || log.snapshot_name || log.id}`

      // Build index: "prefix:identifier" -> log
      const completedMap = new Map<string, (typeof activityLogs)[0]>()
      const startedMap = new Map<string, (typeof activityLogs)[0]>()

      for (const log of logs) {
        for (const [started, completed, failed] of pairs) {
          if (log.event_type === completed || log.event_type === failed) {
            const key = logKey(started, log)
            if (!completedMap.has(key)) completedMap.set(key, log)
          } else if (log.event_type === started) {
            startedMap.set(logKey(started, log), log)
          }
        }
      }

      const merged = new Set<string>()

      return logs.reduce<typeof activityLogs>((acc, log) => {
        // Find which pair this log belongs to
        const pair = pairs.find(
          ([s, c, f]) => log.event_type === s || log.event_type === c || log.event_type === f,
        )
        if (!pair) {
          acc.push(log)
          return acc
        }

        const [startedType] = pair
        const key = logKey(startedType, log)

        // Skip _started if we have a completed/failed for the same snapshot
        if (log.event_type === startedType && completedMap.has(key)) return acc

        // For completed/failed, enrich with start time
        if (log.event_type !== startedType && !merged.has(key)) {
          merged.add(key)
          const started = startedMap.get(key)
          acc.push(
            started ? { ...log, details: { ...log.details, started_at: started.created_at } } : log,
          )
          return acc
        }

        if (!merged.has(key)) acc.push(log)
        return acc
      }, [])
    }

    const mergedLogs = mergePairedLogs(activityLogs)

    const filteredLogs = mergedLogs.filter((log) => {
      if (operationTypeFilter === 'backup' && !isBackupEvent(log.event_type)) return false
      if (operationTypeFilter === 'restore' && isBackupEvent(log.event_type)) return false

      if (searchTerm) {
        const searchLower = searchTerm.toLowerCase()
        return (
          log.snapshot_name?.toLowerCase().includes(searchLower) ||
          log.vm_name?.toLowerCase().includes(searchLower) ||
          log.event_type.toLowerCase().includes(searchLower)
        )
      }
      return true
    })

    return (
      <div className="space-y-4">
        {/* Filters */}
        <div className="flex flex-col gap-3 sm:flex-row">
          <div className="flex-1">
            <div className="relative">
              <SearchIcon className="absolute top-1/2 left-3 size-4 -translate-y-1/2 transform text-gray-400" />
              <input
                type="text"
                placeholder="Buscar logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-white py-2 pr-4 pl-9 text-sm placeholder:text-gray-400 focus:border-purple-500 focus:ring-2 focus:ring-purple-500"
              />
            </div>
          </div>

          {/* Operation Type Filter */}
          <div className="flex items-center rounded-lg bg-gray-100 p-0.5">
            <button
              onClick={() => setOperationTypeFilter('all')}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
                operationTypeFilter === 'all'
                  ? 'bg-white text-gray-900 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Todos
            </button>
            <button
              onClick={() => setOperationTypeFilter('backup')}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
                operationTypeFilter === 'backup'
                  ? 'bg-white text-blue-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <DownloadIcon className="size-3" />
              <span>Backups</span>
            </button>
            <button
              onClick={() => setOperationTypeFilter('restore')}
              className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium transition-all ${
                operationTypeFilter === 'restore'
                  ? 'bg-white text-emerald-700 shadow-sm'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              <RotateCcwIcon className="size-3" />
              <span>Restaurações</span>
            </button>
          </div>

          <button
            onClick={() => refetchActivityLogs()}
            className="inline-flex items-center justify-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-50"
          >
            <RefreshCwIcon className="size-3.5" />
            <span>Atualizar</span>
          </button>
        </div>

        {/* Loading State */}
        {activityLogsLoading && (
          <div className="py-12 text-center">
            <LoaderIcon className="mx-auto size-8 animate-spin text-purple-600" />
            <p className="mt-4 text-gray-600">Carregando logs...</p>
          </div>
        )}

        {/* Empty State */}
        {!activityLogsLoading && filteredLogs.length === 0 && (
          <div className="flex flex-col items-center justify-center rounded-xl border border-dashed border-gray-300 bg-white py-16">
            <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-gray-100">
              <FileArchiveIcon className="size-7 text-gray-400" />
            </div>
            <h3 className="text-base font-semibold text-gray-900">Nenhum log encontrado</h3>
            <p className="mt-1 max-w-xs text-center text-sm text-gray-500">
              {operationTypeFilter === 'all'
                ? 'Histórico de operações aparecerá aqui.'
                : operationTypeFilter === 'backup'
                  ? 'Nenhum log de backup encontrado.'
                  : 'Nenhum log de restauração encontrado.'}
            </p>
          </div>
        )}

        {/* Logs List */}
        {!activityLogsLoading && filteredLogs.length > 0 && (
          <div className="divide-y divide-gray-100 rounded-xl border border-gray-200 bg-white">
            {filteredLogs.map((log) => {
              const { icon: Icon, bg, color } = getEventIcon(log.event_type)
              return (
                <div key={log.id} className="p-5 transition-colors hover:bg-gray-50/50">
                  <div className="flex items-start gap-3">
                    <div
                      className={`size-8 rounded-lg ${bg} mt-0.5 flex shrink-0 items-center justify-center`}
                    >
                      <Icon className={`size-4 ${color}`} />
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="flex items-center gap-2">
                            <h4 className="text-sm font-semibold text-gray-900">
                              {getEventLabel(log.event_type)}
                            </h4>
                          </div>
                          {log.snapshot_name && (
                            <p className="mt-0.5 truncate text-xs text-gray-500">
                              {log.snapshot_name}
                            </p>
                          )}
                        </div>
                        {getStatusBadgeForLog(log.status)}
                      </div>

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                        {log.details?.started_at ? (
                          <>
                            <span className="inline-flex items-center gap-1">
                              <ClockIcon className="size-3" />
                              Início: {formatDate(log.details.started_at as string)}
                            </span>
                            <span className="inline-flex items-center gap-1">
                              <ClockIcon className="size-3" />
                              Fim: {formatDate(log.created_at)}
                            </span>
                            {log.details?.duration_seconds != null && (
                              <span className="inline-flex items-center gap-1 font-medium text-emerald-600">
                                {Number(log.details.duration_seconds) < 60
                                  ? `${log.details.duration_seconds}s`
                                  : `${Math.floor(Number(log.details.duration_seconds) / 60)}m ${Number(log.details.duration_seconds) % 60}s`}
                              </span>
                            )}
                          </>
                        ) : (
                          <span className="inline-flex items-center gap-1">
                            <ClockIcon className="size-3" />
                            {formatDate(log.created_at)}
                          </span>
                        )}
                        {log.vm_name && (
                          <span className="inline-flex items-center gap-1">
                            <ServerIcon className="size-3" />
                            {log.vm_name}
                          </span>
                        )}
                      </div>

                      {log.details && log.status === 'failure' && 'error' in log.details && (
                        <div className="mt-2.5 flex items-start gap-1.5 text-xs text-rose-600">
                          <AlertCircleIcon className="mt-0.5 size-3.5 shrink-0" />
                          <span>{String(log.details.error)}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    )
  }

  // Format currency
  const formatCurrency = (value: number, currency: string = 'BRL') => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency,
    }).format(value ?? 0)
  }

  // Format GB
  const formatGB = (gb: number) => {
    return (gb ?? 0).toFixed(2)
  }

  // Generate period options (last 12 months)
  const generatePeriodOptions = () => {
    const options = []
    const now = new Date()
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1)
      const period = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
      const label = date.toLocaleDateString('pt-BR', { year: 'numeric', month: 'long' })
      options.push({ value: period, label })
    }
    return options
  }

  const renderUsageTab = () => (
    <div className="space-y-5">
      {/* Period Selector */}
      <div className="rounded-xl border border-gray-200 bg-white p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <label className="text-sm font-medium text-gray-700">Período de Faturamento</label>
          <select
            value={selectedPeriod}
            onChange={(e) => setSelectedPeriod(e.target.value)}
            className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500 sm:w-56"
          >
            {generatePeriodOptions().map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      {usageLoading && (
        <div className="flex flex-col items-center justify-center py-16">
          <LoaderIcon className="size-7 animate-spin text-purple-600" />
          <p className="mt-3 text-sm text-gray-500">Carregando uso de backup...</p>
        </div>
      )}

      {!usageLoading && usageSummary && (
        <>
          {/* Summary Cards */}
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white p-5">
              <div className="absolute inset-x-0 top-0 h-0.5 bg-blue-500" />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                    Storage Máximo
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900 tabular-nums">
                    {formatGB(usageSummary.total_storage_gb ?? usageSummary.max_storage_gb ?? 0)} GB
                  </p>
                </div>
                <div className="flex size-10 items-center justify-center rounded-lg bg-blue-50">
                  <HardDriveIcon className="size-5 text-blue-600" />
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white p-5">
              <div className="absolute inset-x-0 top-0 h-0.5 bg-emerald-500" />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                    Custo Total
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900 tabular-nums">
                    {formatCurrency(usageSummary.total_cost, usageSummary.currency)}
                  </p>
                </div>
                <div className="flex size-10 items-center justify-center rounded-lg bg-emerald-50">
                  <DollarSignIcon className="size-5 text-emerald-600" />
                </div>
              </div>
            </div>

            <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white p-5">
              <div className="absolute inset-x-0 top-0 h-0.5 bg-purple-500" />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                    VMs com Backup
                  </p>
                  <p className="mt-1 text-2xl font-semibold text-gray-900 tabular-nums">
                    {usageSummary.total_vms_with_backup ?? usageSummary.total_vms ?? 0}
                  </p>
                </div>
                <div className="flex size-10 items-center justify-center rounded-lg bg-purple-50">
                  <ServerIcon className="size-5 text-purple-600" />
                </div>
              </div>
            </div>
          </div>

          {/* Usage Chart */}
          {dailyUsage.length > 1 && (
            <div className="rounded-xl border border-gray-200 bg-white p-5">
              <h2 className="mb-1 text-sm font-semibold text-gray-900">Evolução de Storage</h2>
              <p className="mb-4 text-xs text-gray-500">
                Consumo diário de armazenamento no período
              </p>
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={dailyUsage} margin={{ top: 5, right: 5, left: -10, bottom: 0 }}>
                    <defs>
                      <linearGradient id="storageGradient" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.15} />
                        <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                    <XAxis
                      dataKey="date"
                      tick={{ fontSize: 11, fill: '#9ca3af' }}
                      tickFormatter={(d: string) => {
                        const day = d.split('-')[2]
                        return `${Number.parseInt(day)}`
                      }}
                    />
                    <YAxis
                      tick={{ fontSize: 11, fill: '#9ca3af' }}
                      tickFormatter={(v: number) => `${v.toFixed(0)} GB`}
                    />
                    <Tooltip
                      contentStyle={{ fontSize: 12, borderRadius: 8, border: '1px solid #e5e7eb' }}
                      formatter={(value) => [`${Number(value ?? 0).toFixed(2)} GB`, 'Storage']}
                      labelFormatter={(label) => {
                        const d = new Date(`${String(label)}T00:00:00`)
                        return d.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="storage_gb"
                      stroke="#8b5cf6"
                      strokeWidth={2}
                      fill="url(#storageGradient)"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </div>
          )}

          {/* Breakdown by VM */}
          <div className="rounded-xl border border-gray-200 bg-white">
            <div className="border-b border-gray-100 px-5 py-4">
              <h2 className="text-sm font-semibold text-gray-900">Detalhamento por VM</h2>
              <p className="mt-0.5 text-xs text-gray-500">
                Consumo de armazenamento e custos por máquina virtual
              </p>
            </div>

            {usageSummary.breakdown_by_vm && usageSummary.breakdown_by_vm.length > 0 ? (
              <div>
                <div className="divide-y divide-gray-200 md:hidden">
                  {usageSummary.breakdown_by_vm.map((vm) => {
                    const percentage =
                      usageSummary.total_cost > 0
                        ? (vm.total_cost / usageSummary.total_cost) * 100
                        : 0
                    return (
                      <div key={vm.vm_id} className="space-y-2 p-4">
                        <div className="flex items-center gap-2">
                          <ServerIcon className="size-4 text-gray-400" />
                          <div className="min-w-0">
                            <div className="truncate text-sm font-medium text-gray-900">
                              {vm.vm_name}
                            </div>
                            <div className="truncate text-xs text-gray-500">{vm.vm_id}</div>
                          </div>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-sm">
                          <div>
                            <p className="text-gray-500">Storage Alocado</p>
                            <p className="font-medium text-gray-900">
                              {formatGB(vm.total_storage_gb)} GB
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">Snapshots Ativos</p>
                            <p className="font-medium text-gray-900">{vm.snapshot_count}</p>
                          </div>
                          <div>
                            <p className="text-gray-500">Custo</p>
                            <p className="font-medium text-gray-900">
                              {formatCurrency(vm.total_cost, usageSummary.currency)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-500">% do Total</p>
                            <p className="font-medium text-gray-900">{percentage.toFixed(1)}%</p>
                          </div>
                        </div>
                      </div>
                    )
                  })}
                </div>
                <div className="hidden overflow-x-auto md:block">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                          VM
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                          Storage Alocado
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                          Snapshots Ativos
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                          Custo
                        </th>
                        <th className="px-6 py-3 text-left text-xs font-medium tracking-wider text-gray-500 uppercase">
                          % do Total
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {usageSummary.breakdown_by_vm.map((vm) => {
                        const percentage =
                          usageSummary.total_cost > 0
                            ? (vm.total_cost / usageSummary.total_cost) * 100
                            : 0

                        return (
                          <tr key={vm.vm_id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <ServerIcon className="mr-3 size-5 text-gray-400" />
                                <div>
                                  <div className="text-sm font-medium text-gray-900">
                                    {vm.vm_name}
                                  </div>
                                  <div className="text-xs text-gray-500">{vm.vm_id}</div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">
                                {formatGB(vm.total_storage_gb)} GB
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm text-gray-900">{vm.snapshot_count}</div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="text-sm font-medium text-gray-900">
                                {formatCurrency(vm.total_cost, usageSummary.currency)}
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center">
                                <div className="mr-2 h-2 w-16 rounded-full bg-gray-200">
                                  <div
                                    className="h-2 rounded-full bg-blue-600"
                                    style={{ width: `${percentage}%` }}
                                  />
                                </div>
                                <span className="text-sm text-gray-600">
                                  {percentage.toFixed(1)}%
                                </span>
                              </div>
                            </td>
                          </tr>
                        )
                      })}
                    </tbody>
                  </table>
                </div>
              </div>
            ) : (
              <div className="p-12 text-center">
                <HardDriveIcon className="mx-auto mb-4 size-16 text-gray-400" />
                <h3 className="mb-2 text-lg font-medium text-gray-900">Nenhum backup encontrado</h3>
                <p className="text-gray-600">Não há dados de backup para o período selecionado</p>
              </div>
            )}
          </div>

          {/* Info Box */}
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="flex gap-3">
              <div className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-blue-50">
                <InfoIcon className="size-4 text-blue-600" />
              </div>
              <div>
                <h3 className="mb-1.5 text-xs font-semibold text-gray-900">
                  Como funciona a cobrança de backup?
                </h3>
                <div className="space-y-1 text-xs text-gray-600">
                  <p>
                    <span className="font-medium text-gray-700">Pay-as-you-go</span> — Pague apenas
                    pelo armazenamento utilizado
                  </p>
                  <p>
                    <span className="font-medium text-gray-700">Cobrança</span> — Baseada no tamanho
                    dos discos das VMs com backup ativo
                  </p>
                  <p>
                    <span className="font-medium text-gray-700">Preço</span> —{' '}
                    {usageSummary?.price_per_gb == null
                      ? 'Consulte seu reseller'
                      : `R$ ${usageSummary.price_per_gb.toFixed(2).replace('.', ',')}`}{' '}
                    por GB/mês (cobrado pelo pico de consumo no mês)
                  </p>
                  <p>
                    <span className="font-medium text-gray-700">Faturamento</span> — Processado
                    mensalmente com base no uso do período
                  </p>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {!usageLoading && !usageSummary && (
        <div className="rounded-lg bg-white py-12 text-center shadow">
          <HardDriveIcon className="mx-auto mb-4 size-16 text-gray-400" />
          <h3 className="mb-2 text-lg font-medium text-gray-900">Sem dados disponíveis</h3>
          <p className="mb-6 text-gray-600">
            Não há dados de uso de backup para o período selecionado
          </p>
        </div>
      )}
    </div>
  )

  const renderAlertsTab = () => (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col gap-3 sm:flex-row">
        <select
          value={alertsStatusFilter || ''}
          onChange={(e) => setAlertsStatusFilter((e.target.value as AlertStatus) || undefined)}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500 sm:w-auto"
        >
          <option value="">Todos os status</option>
          <option value="active">Ativo</option>
          <option value="acknowledged">Reconhecido</option>
          <option value="resolved">Resolvido</option>
          <option value="dismissed">Descartado</option>
        </select>

        <select
          value={alertsSeverityFilter || ''}
          onChange={(e) => setAlertsSeverityFilter((e.target.value as AlertSeverity) || undefined)}
          className="w-full rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm focus:border-purple-500 focus:ring-2 focus:ring-purple-500 sm:w-auto"
        >
          <option value="">Todas as Severidades</option>
          <option value="info">Informação</option>
          <option value="warning">Atenção</option>
          <option value="critical">Crítico</option>
        </select>

        {(alertsStatusFilter || alertsSeverityFilter) && (
          <button
            onClick={() => {
              setAlertsStatusFilter(undefined)
              setAlertsSeverityFilter(undefined)
            }}
            className="inline-flex items-center gap-1.5 rounded-lg px-3 py-2 text-sm text-gray-600 transition-colors hover:bg-gray-100"
          >
            <XIcon className="size-3.5" />
            <span>Limpar</span>
          </button>
        )}
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white p-4">
          <div className="absolute inset-x-0 top-0 h-0.5 bg-amber-500" />
          <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">Total</p>
          <p className="mt-1 text-xl font-semibold text-gray-900 tabular-nums">{alerts.length}</p>
        </div>

        <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white p-4">
          <div className="absolute inset-x-0 top-0 h-0.5 bg-rose-500" />
          <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">Críticos</p>
          <p className="mt-1 text-xl font-semibold text-rose-600 tabular-nums">
            {alerts.filter((a) => a.severity === 'critical').length}
          </p>
        </div>

        <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white p-4">
          <div className="absolute inset-x-0 top-0 h-0.5 bg-amber-400" />
          <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">Atenção</p>
          <p className="mt-1 text-xl font-semibold text-amber-600 tabular-nums">
            {alerts.filter((a) => a.severity === 'warning').length}
          </p>
        </div>

        <div className="relative overflow-hidden rounded-xl border border-gray-200 bg-white p-4">
          <div className="absolute inset-x-0 top-0 h-0.5 bg-emerald-500" />
          <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">Resolvidos</p>
          <p className="mt-1 text-xl font-semibold text-emerald-600 tabular-nums">
            {alerts.filter((a) => a.status === 'resolved').length}
          </p>
        </div>
      </div>

      {/* Alerts List */}
      <div className="rounded-xl border border-gray-200 bg-white">
        {alertsLoading ? (
          <div className="flex flex-col items-center justify-center py-16">
            <LoaderIcon className="size-7 animate-spin text-purple-600" />
            <p className="mt-3 text-sm text-gray-500">Carregando alertas...</p>
          </div>
        ) : alerts.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16">
            <div className="mb-4 flex size-14 items-center justify-center rounded-full bg-emerald-50">
              <CheckCircleIcon className="size-7 text-emerald-500" />
            </div>
            <h3 className="text-base font-semibold text-gray-900">Nenhum alerta</h3>
            <p className="mt-1 max-w-xs text-center text-sm text-gray-500">
              Nenhum alerta de uso excessivo no momento.
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-100">
            {alerts.map((alert) => {
              const severityBorderMap: Record<string, string> = {
                critical: 'border-l-rose-500',
                warning: 'border-l-amber-400',
                info: 'border-l-blue-400',
              }

              return (
                <div
                  key={alert.id}
                  className={`cursor-pointer border-l-2 px-5 py-4 transition-colors hover:bg-gray-50/50 ${severityBorderMap[alert.severity] || 'border-l-gray-200'}`}
                  onClick={() => {
                    setSelectedAlert(alert)
                    setShowAlertDetailsModal(true)
                  }}
                >
                  <div className="flex items-start gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="mb-2 flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h4 className="truncate text-sm font-semibold text-gray-900">
                            {alert.title}
                          </h4>
                          <p className="mt-0.5 line-clamp-1 text-xs text-gray-500">
                            {alert.message}
                          </p>
                        </div>
                        <div className="flex shrink-0 items-center gap-1.5">
                          <span
                            className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${getAlertSeverityColor(alert.severity)}`}
                          >
                            {getAlertSeverityLabel(alert.severity)}
                          </span>
                          <span
                            className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-medium ring-1 ring-inset ${getAlertStatusColor(alert.status)}`}
                          >
                            {getAlertStatusLabel(alert.status)}
                          </span>
                        </div>
                      </div>

                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                        <span>{getAlertTypeLabel(alert.alert_type)}</span>
                        {alert.vm_name && (
                          <span className="inline-flex items-center gap-1">
                            <ServerIcon className="size-3" />
                            {alert.vm_name}
                          </span>
                        )}
                        {alert.change_percentage !== null && (
                          <span
                            className={`font-medium ${
                              alert.change_percentage >= 100
                                ? 'text-rose-600'
                                : alert.change_percentage >= 75
                                  ? 'text-amber-600'
                                  : 'text-blue-600'
                            }`}
                          >
                            {formatGrowthPercentage(alert.change_percentage)}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1">
                          <ClockIcon className="size-3" />
                          {formatAlertDate(alert.detected_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )

  // ========================================
  // Main Render
  // ========================================

  return (
    <div className="mobile-page-padding">
      <ResponsivePageHeader
        title="Backups"
        description="Gerencie backups, agendamentos e restaurações das suas VMs"
        icon={ShieldIcon}
        actions={
          <div className="flex flex-col items-end gap-1">
            <button
              onClick={() => setAutoRefresh(!autoRefresh)}
              className={`inline-flex items-center rounded-lg px-3 py-1.5 text-xs font-medium ${
                autoRefresh ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'
              }`}
            >
              Auto-refresh {autoRefresh ? 'ligado' : 'desligado'}
            </button>
            <div className="text-[10px] text-gray-500">
              Última atualização:{' '}
              {snapshotsUpdatedAt
                ? new Date(snapshotsUpdatedAt).toLocaleTimeString('pt-BR')
                : '--:--:--'}
            </div>
          </div>
        }
      />

      {/* Stats Cards */}
      <div className="mb-6 grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-5 transition-colors hover:border-gray-300">
          <div className="absolute inset-x-0 top-0 h-0.5 bg-blue-500" />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                Pontos de Restauração
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 tabular-nums">
                {snapshots.length}
              </p>
            </div>
            <div className="flex size-10 items-center justify-center rounded-lg bg-blue-50">
              <HardDriveIcon className="size-5 text-blue-600" />
            </div>
          </div>
        </div>

        <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-5 transition-colors hover:border-gray-300">
          <div className="absolute inset-x-0 top-0 h-0.5 bg-purple-500" />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">
                Storage Ativo
              </p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 tabular-nums">
                {(() => {
                  const totalBytes = snapshots.reduce((acc, s) => acc + (s.size_bytes ?? 0), 0)
                  const totalGb = totalBytes / 1024 ** 3
                  return totalGb > 0 ? `${formatGB(totalGb)} GB` : '0 GB'
                })()}
              </p>
            </div>
            <div className="flex size-10 items-center justify-center rounded-lg bg-purple-50">
              <DatabaseIcon className="size-5 text-purple-600" />
            </div>
          </div>
        </div>

        <div className="group relative overflow-hidden rounded-xl border border-gray-200 bg-white p-5 transition-colors hover:border-gray-300">
          <div className="absolute inset-x-0 top-0 h-0.5 bg-amber-500" />
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-medium tracking-wide text-gray-500 uppercase">Jobs</p>
              <p className="mt-1 text-2xl font-semibold text-gray-900 tabular-nums">
                {backupJobs.length}
              </p>
            </div>
            <div className="flex size-10 items-center justify-center rounded-lg bg-amber-50">
              <CalendarIcon className="size-5 text-amber-600" />
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <ResponsiveTabs
          tabs={tabs}
          value={activeTab}
          onChange={setActiveTab}
          ariaLabel="Abas de backups"
        />
      </div>

      {/* Content */}
      <div>
        {activeTab === 'snapshots' && renderSnapshotsTab()}
        {activeTab === 'jobs' && renderJobsTab()}
        {activeTab === 'logs' && renderLogsTab()}
        {activeTab === 'usage' && renderUsageTab()}
        {activeTab === 'alerts' && renderAlertsTab()}
      </div>

      {/* Modal de Job de Backup */}
      {showJobModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div className="mobile-modal-surface flex max-h-[90vh] w-full max-w-2xl flex-col rounded-2xl bg-white shadow-xl">
            <div className="shrink-0 border-b border-gray-100 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">
                    {editingJob ? 'Editar Job de Backup' : 'Novo Job de Backup'}
                  </h3>
                  <p className="mt-0.5 text-xs text-gray-500">
                    Configure um job para agendar backups de múltiplas VMs
                  </p>
                </div>
                <button
                  onClick={() => {
                    setShowJobModal(false)
                    resetJobForm()
                  }}
                  className="flex size-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                >
                  <XIcon className="size-4" />
                </button>
              </div>
            </div>
            <div className="flex-1 space-y-4 overflow-y-auto p-6">
              {/* Job Name */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Nome do Job *
                </label>
                <input
                  type="text"
                  value={jobForm.name}
                  onChange={(e) => setJobForm((prev) => ({ ...prev, name: e.target.value }))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  placeholder="ex: Backup Diário Produção"
                />
              </div>

              {/* VM Selection */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Máquinas Virtuais ({(jobForm.vm_ids || []).length}/50)
                </label>
                <div className="max-h-48 overflow-y-auto rounded-lg border border-gray-200">
                  {vmsLoading ? (
                    <p className="p-3 text-xs text-gray-500">Carregando VMs...</p>
                  ) : customerVMs.length === 0 ? (
                    <p className="p-3 text-xs text-gray-500">Nenhuma VM disponível.</p>
                  ) : (
                    customerVMs.map((vm) => {
                      const isSelected = (jobForm.vm_ids || []).includes(vm.id)
                      return (
                        <label
                          key={vm.id}
                          className={`flex cursor-pointer items-center gap-3 border-b border-gray-50 px-3 py-2 last:border-b-0 hover:bg-gray-50 ${
                            isSelected ? 'bg-purple-50' : ''
                          }`}
                        >
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={(e) => {
                              const vmIds = jobForm.vm_ids || []
                              if (e.target.checked) {
                                if (vmIds.length >= 50) {
                                  toast.error('Máximo de 50 VMs por job')
                                  return
                                }
                                setJobForm((prev) => ({ ...prev, vm_ids: [...vmIds, vm.id] }))
                              } else {
                                setJobForm((prev) => ({
                                  ...prev,
                                  vm_ids: vmIds.filter((id) => id !== vm.id),
                                }))
                              }
                            }}
                            className="rounded text-purple-600 focus:ring-purple-500"
                          />
                          <ServerIcon className="size-3.5 shrink-0 text-gray-400" />
                          <span className="truncate text-sm text-gray-900">{vm.name}</span>
                          <span
                            className={`ml-auto rounded px-1.5 py-0.5 text-xs ${
                              vm.status === 'running'
                                ? 'bg-emerald-50 text-emerald-700'
                                : 'bg-gray-100 text-gray-500'
                            }`}
                          >
                            {vm.status}
                          </span>
                        </label>
                      )
                    })
                  )}
                </div>
              </div>

              {/* Priority Order */}
              {(jobForm.vm_ids || []).length > 0 && (
                <div>
                  <label className="mb-2 block text-sm font-medium text-gray-700">
                    Ordem de execução
                  </label>
                  <div className="divide-y divide-gray-100 rounded-lg border border-gray-200">
                    {(jobForm.vm_ids || []).map((vmId, idx) => {
                      const vm = customerVMs.find((v) => v.id === vmId)
                      const jobVm = editingJob?.vms?.find((v) => v.vm_id === vmId)
                      const vmName = vm?.name || jobVm?.vm_name || vmId.slice(0, 8)
                      const isRemoved = !vm && isVMRemovida(jobVm)
                      const isFirst = idx === 0
                      const isLast = idx === (jobForm.vm_ids || []).length - 1
                      return (
                        <div key={vmId} className="flex items-center gap-3 px-3 py-2">
                          <span className="w-5 shrink-0 text-center font-mono text-xs text-gray-400">
                            #{idx + 1}
                          </span>
                          <ServerIcon
                            className={`size-3.5 shrink-0 ${isRemoved ? 'text-rose-300' : 'text-gray-400'}`}
                          />
                          <span
                            className={`flex-1 truncate text-sm ${isRemoved ? 'text-gray-400 line-through' : 'text-gray-900'}`}
                          >
                            {vmName}
                          </span>
                          <div className="flex shrink-0 items-center gap-0.5">
                            <button
                              type="button"
                              disabled={isFirst}
                              onClick={() => {
                                const ids = [...(jobForm.vm_ids || [])]
                                ;[ids[idx - 1], ids[idx]] = [ids[idx], ids[idx - 1]]
                                setJobForm((prev) => ({ ...prev, vm_ids: ids }))
                              }}
                              className="p-0.5 text-gray-400 transition-colors hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-20"
                              title="Subir prioridade"
                            >
                              <ChevronUpIcon className="size-4" />
                            </button>
                            <button
                              type="button"
                              disabled={isLast}
                              onClick={() => {
                                const ids = [...(jobForm.vm_ids || [])]
                                ;[ids[idx], ids[idx + 1]] = [ids[idx + 1], ids[idx]]
                                setJobForm((prev) => ({ ...prev, vm_ids: ids }))
                              }}
                              className="p-0.5 text-gray-400 transition-colors hover:text-gray-700 disabled:cursor-not-allowed disabled:opacity-20"
                              title="Descer prioridade"
                            >
                              <ChevronDownIcon className="size-4" />
                            </button>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                  <p className="mt-1 text-xs text-gray-500">
                    A ordem define a prioridade de execução do backup.
                  </p>
                </div>
              )}

              {/* Backup Frequency */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">Frequência *</label>
                <div className="grid grid-cols-3 gap-3">
                  {(
                    [
                      { key: 'daily', label: 'Diário', desc: 'Todo dia' },
                      { key: 'weekly', label: 'Semanal', desc: 'Uma vez por semana' },
                      { key: 'monthly', label: 'Mensal', desc: 'Dia 1 de cada mês' },
                    ] as const
                  ).map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => setJobFrequency(opt.key)}
                      className={`rounded-lg border-2 px-4 py-3 transition-colors ${
                        jobFrequency === opt.key
                          ? 'border-purple-600 bg-purple-50 text-purple-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-sm font-medium">{opt.label}</div>
                      <div className="text-xs text-gray-500">{opt.desc}</div>
                    </button>
                  ))}
                </div>
              </div>

              {/* Time Selection */}
              <div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Horário *
                    </label>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <label className="mb-1 block text-xs text-gray-500">Hora</label>
                        <select
                          value={jobTime.split(':')[0]}
                          onChange={(e) => {
                            const minute = jobTime.split(':')[1] || '00'
                            setJobTime(`${e.target.value}:${minute}`)
                          }}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
                        >
                          {Array.from({ length: 24 }, (_, i) => {
                            const hour = String(i).padStart(2, '0')
                            return (
                              <option key={hour} value={hour}>
                                {hour}
                              </option>
                            )
                          })}
                        </select>
                      </div>
                      <div>
                        <label className="mb-1 block text-xs text-gray-500">Minuto</label>
                        <select
                          value={jobTime.split(':')[1]}
                          onChange={(e) => {
                            const hour = jobTime.split(':')[0] || '02'
                            setJobTime(`${hour}:${e.target.value}`)
                          }}
                          className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
                        >
                          {[
                            '00',
                            '05',
                            '10',
                            '15',
                            '20',
                            '25',
                            '30',
                            '35',
                            '40',
                            '45',
                            '50',
                            '55',
                          ].map((min) => (
                            <option key={min} value={min}>
                              {min}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  </div>
                  {jobFrequency === 'weekly' && (
                    <div>
                      <label className="mb-1 block text-sm font-medium text-gray-700">
                        Dia da Semana *
                      </label>
                      <select
                        value={jobWeekDay}
                        onChange={(e) => setJobWeekDay(e.target.value)}
                        className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
                      >
                        <option value="0">Domingo</option>
                        <option value="1">Segunda-feira</option>
                        <option value="2">Terça-feira</option>
                        <option value="3">Quarta-feira</option>
                        <option value="4">Quinta-feira</option>
                        <option value="5">Sexta-feira</option>
                        <option value="6">Sábado</option>
                      </select>
                    </div>
                  )}
                </div>
              </div>

              {/* Preview */}
              <div className="rounded-lg border border-blue-200 bg-blue-50 p-3">
                <p className="text-xs text-blue-900">
                  <strong>Agendamento:</strong> {formatCronDescription(jobForm.cron_expression)}
                </p>
              </div>

              {/* Retention Policy */}
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Política de Retenção *
                </label>
                <div className="grid grid-cols-3 gap-3">
                  {(
                    [
                      { key: '7days', label: '7 dias', desc: '1 semana' },
                      { key: '15days', label: '15 dias', desc: '2 semanas' },
                      { key: '30days', label: '30 dias', desc: '1 mês' },
                      { key: '60days', label: '60 dias', desc: '2 meses' },
                      { key: '90days', label: '90 dias', desc: '3 meses' },
                      { key: 'custom', label: 'Personalizado', desc: 'Avançado' },
                    ] as const
                  ).map((opt) => (
                    <button
                      key={opt.key}
                      type="button"
                      onClick={() => {
                        setJobRetentionPreset(opt.key)
                        if (opt.key === 'custom') setShowJobAdvancedRetention(true)
                      }}
                      className={`rounded-lg border-2 px-4 py-3 transition-colors ${
                        jobRetentionPreset === opt.key
                          ? 'border-blue-600 bg-blue-50 text-blue-700'
                          : 'border-gray-200 hover:border-gray-300'
                      }`}
                    >
                      <div className="text-sm font-medium">{opt.label}</div>
                      <div className="text-xs text-gray-500">{opt.desc}</div>
                    </button>
                  ))}
                </div>

                {showJobAdvancedRetention && (
                  <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <h4 className="text-sm font-medium text-gray-900">Configuração Avançada</h4>
                      <button
                        type="button"
                        onClick={() => setShowJobAdvancedRetention(false)}
                        className="text-xs text-gray-500 hover:text-gray-700"
                      >
                        Ocultar
                      </button>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      {[
                        {
                          key: 'keep_last',
                          label: 'Últimos N backups',
                          desc: 'Manter sempre os N mais recentes',
                        },
                        {
                          key: 'keep_daily',
                          label: 'Backups diários',
                          desc: 'Um por dia (últimos N dias)',
                        },
                        {
                          key: 'keep_weekly',
                          label: 'Backups semanais',
                          desc: 'Um por semana (últimas N semanas)',
                        },
                        {
                          key: 'keep_monthly',
                          label: 'Backups mensais',
                          desc: 'Um por mês (últimos N meses)',
                        },
                      ].map((field) => (
                        <div key={field.key}>
                          <label className="mb-1 block text-xs font-medium text-gray-700">
                            {field.label}
                          </label>
                          <input
                            type="number"
                            value={(jobForm as any)[field.key]}
                            onChange={(e) =>
                              setJobForm((prev) => ({
                                ...prev,
                                [field.key]: Number.parseInt(e.target.value) || 0,
                              }))
                            }
                            className="w-full rounded border border-gray-300 px-2 py-1 text-sm focus:ring-2 focus:ring-blue-500"
                            min="0"
                          />
                          <p className="mt-1 text-xs text-gray-500">{field.desc}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Stop on Failure */}
              <div className="flex items-center gap-3">
                <input
                  type="checkbox"
                  id="stopOnFailure"
                  checked={jobForm.stop_on_failure}
                  onChange={(e) =>
                    setJobForm((prev) => ({ ...prev, stop_on_failure: e.target.checked }))
                  }
                  className="rounded text-purple-600 focus:ring-purple-500"
                />
                <label htmlFor="stopOnFailure" className="text-sm text-gray-700">
                  Parar execução se uma VM falhar
                </label>
              </div>

              {/* Description */}
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Descrição</label>
                <textarea
                  value={jobForm.description || ''}
                  onChange={(e) => setJobForm((prev) => ({ ...prev, description: e.target.value }))}
                  rows={2}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
                  placeholder="Descrição opcional..."
                />
              </div>
            </div>

            <div className="flex shrink-0 justify-end gap-2.5 rounded-b-2xl border-t border-gray-100 bg-gray-50/50 px-6 py-4">
              <button
                onClick={() => {
                  setShowJobModal(false)
                  resetJobForm()
                }}
                disabled={createJobMutation.isPending || updateJobMutation.isPending}
                className="rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={editingJob ? handleUpdateJob : handleCreateJob}
                disabled={
                  !jobForm.name ||
                  (jobForm.vm_ids || []).length === 0 ||
                  createJobMutation.isPending ||
                  updateJobMutation.isPending
                }
                className="inline-flex items-center gap-2 rounded-lg bg-purple-600 px-5 py-2 text-sm font-medium text-white transition-colors hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-200 disabled:text-gray-400"
              >
                {(createJobMutation.isPending || updateJobMutation.isPending) && (
                  <LoaderIcon className="size-3.5 animate-spin" />
                )}
                <span>
                  {editingJob
                    ? updateJobMutation.isPending
                      ? 'Salvando...'
                      : 'Salvar'
                    : createJobMutation.isPending
                      ? 'Criando...'
                      : 'Criar Job'}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Delete Job */}
      <ConfirmModal
        isOpen={!!jobToDelete}
        title="Deletar Job de Backup"
        message={
          jobToDelete
            ? `Tem certeza que deseja deletar o job "${jobToDelete.name}"? Os backups existentes serão mantidos, mas novos backups não serão criados.`
            : ''
        }
        confirmText="Deletar"
        variant="danger"
        isLoading={deleteJobMutation.isPending}
        onConfirm={() => {
          if (jobToDelete) {
            deleteJobMutation.mutate(jobToDelete.id, {
              onSuccess: () => setJobToDelete(null),
            })
          }
        }}
        onClose={() => setJobToDelete(null)}
      />

      {/* Modal de restauração */}
      {showRestoreModal && selectedSnapshot && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 backdrop-blur-sm">
          <div className="mobile-modal-surface w-full max-w-2xl rounded-2xl bg-white shadow-xl">
            <div className="border-b border-gray-100 px-6 py-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-base font-semibold text-gray-900">Restaurar Snapshot</h3>
                  <p className="mt-0.5 text-xs text-gray-500">{selectedSnapshot.name}</p>
                </div>
                <button
                  onClick={() => setShowRestoreModal(false)}
                  className="flex size-8 items-center justify-center rounded-lg text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600"
                >
                  <XIcon className="size-4" />
                </button>
              </div>
            </div>

            <div className="space-y-6 p-6">
              <div>
                <label className="mb-2 block text-sm font-medium text-gray-700">
                  Tipo de Restauração
                </label>
                <div className="grid grid-cols-1 gap-3">
                  <label className="flex cursor-pointer items-center space-x-3 rounded-lg border p-3 hover:bg-gray-50">
                    <input
                      type="radio"
                      name="restoreType"
                      value="full"
                      checked={restoreForm.restore_type === 'full'}
                      onChange={(e) =>
                        setRestoreForm((prev) => ({
                          ...prev,
                          restore_type: e.target.value,
                          replace_existing: true, // Restauração completa sempre substitui
                          restore_network_config: true, // Full restore mantém config original
                        }))
                      }
                      className="text-purple-600"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">Restauração Completa</div>
                      <div className="text-xs text-gray-500">
                        Substitui completamente a VM existente
                      </div>
                    </div>
                  </label>
                  <label className="flex cursor-pointer items-center space-x-3 rounded-lg border p-3 hover:bg-gray-50">
                    <input
                      type="radio"
                      name="restoreType"
                      value="clone"
                      checked={restoreForm.restore_type === 'clone'}
                      onChange={(e) =>
                        setRestoreForm((prev) => ({
                          ...prev,
                          restore_type: e.target.value,
                          replace_existing: false, // Clone não substitui
                          restore_network_config: false, // Default: resetar IP para DHCP
                        }))
                      }
                      className="text-purple-600"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">Clonar para Nova VM</div>
                      <div className="text-xs text-gray-500">
                        Cria uma nova VM a partir do snapshot
                      </div>
                    </div>
                  </label>
                </div>
              </div>

              {restoreForm.restore_type === 'full' && (
                <div>
                  <label className="mb-1 block text-sm font-medium text-gray-700">
                    VM de Destino (ID)
                  </label>
                  <input
                    type="text"
                    value={restoreForm.target_vm_id || ''}
                    onChange={(e) =>
                      setRestoreForm((prev) => ({ ...prev, target_vm_id: e.target.value }))
                    }
                    className="w-full rounded-lg border border-gray-300 bg-gray-50 px-3 py-2 focus:ring-2 focus:ring-purple-500"
                    placeholder="UUID da VM de destino"
                    readOnly
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    ℹ️ Restauração será feita na VM original do backup
                  </p>
                </div>
              )}

              {restoreForm.restore_type === 'clone' && (
                <div className="space-y-4">
                  <div>
                    <label className="mb-1 block text-sm font-medium text-gray-700">
                      Nome da Nova VM
                    </label>
                    <input
                      type="text"
                      value={restoreForm.new_vm_name || ''}
                      onChange={(e) =>
                        setRestoreForm((prev) => ({ ...prev, new_vm_name: e.target.value }))
                      }
                      className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
                      placeholder="ex: web-server-clone"
                    />
                  </div>

                  <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
                    <div className="flex items-start space-x-3">
                      <AlertTriangleIcon className="mt-0.5 size-5 shrink-0 text-amber-600" />
                      <div className="space-y-2">
                        <h4 className="text-sm font-medium text-amber-800">
                          Configuração de Rede do Clone
                        </h4>
                        <p className="text-sm text-amber-700">
                          O clone recebe <strong>novos endereços MAC</strong> automaticamente para
                          evitar conflitos. Se a VM original usa <strong>IP estático</strong> (via
                          cloud-init ou configuração manual), o clone terá o mesmo IP, causando
                          conflito na rede.
                        </p>
                        <label className="mt-2 flex cursor-pointer items-center space-x-2">
                          <input
                            type="checkbox"
                            checked={!restoreForm.restore_network_config}
                            onChange={(e) =>
                              setRestoreForm((prev) => ({
                                ...prev,
                                restore_network_config: !e.target.checked,
                              }))
                            }
                            className="rounded text-amber-600 focus:ring-amber-500"
                          />
                          <span className="text-sm font-medium text-amber-800">
                            Resetar IP para DHCP (recomendado)
                          </span>
                        </label>
                        {!restoreForm.restore_network_config && (
                          <p className="text-xs text-amber-600">
                            O cloud-init será reconfigurado para obter IP via DHCP. As interfaces de
                            rede e bridges serão mantidas.
                          </p>
                        )}
                        {restoreForm.restore_network_config && (
                          <p className="text-xs font-medium text-red-600">
                            ⚠️ A configuração de IP original será mantida. Se a VM original estiver
                            ativa na mesma rede, haverá conflito de IP.
                          </p>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="rounded-lg bg-gray-50 p-4">
                <h4 className="mb-3 text-sm font-medium text-gray-700">Opções</h4>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="startAfterRestore"
                    checked={restoreForm.start_after_restore}
                    onChange={(e) =>
                      setRestoreForm((prev) => ({ ...prev, start_after_restore: e.target.checked }))
                    }
                    className="rounded"
                  />
                  <label htmlFor="startAfterRestore" className="text-sm text-gray-700">
                    Iniciar VM após restauração
                  </label>
                </div>
              </div>

              {restoreForm.restore_type === 'full' ? (
                <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                  <div className="flex items-start space-x-3">
                    <AlertTriangleIcon className="mt-0.5 size-5 text-red-600" />
                    <div>
                      <h4 className="text-sm font-medium text-red-800">⚠️ Operação Irreversível</h4>
                      <p className="mt-1 text-sm text-red-700">
                        A restauração completa{' '}
                        <strong>substituirá todos os dados atuais da VM</strong>. Esta operação não
                        pode ser desfeita. A VM ficará indisponível durante o processo.
                      </p>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <div className="flex items-start space-x-3">
                    <AlertTriangleIcon className="mt-0.5 size-5 text-blue-600" />
                    <div>
                      <h4 className="text-sm font-medium text-blue-800">ℹ️ Nova VM</h4>
                      <p className="mt-1 text-sm text-blue-700">
                        Será criada uma nova VM a partir do backup. A VM original não será afetada.
                        Esta operação pode levar alguns minutos.
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end space-x-3 border-t bg-gray-50 p-6">
              <button
                onClick={() => setShowRestoreModal(false)}
                disabled={initiateRestoreMutation.isPending}
                className="rounded-lg border border-gray-300 px-4 py-2 hover:bg-gray-50 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                onClick={handleInitiateRestore}
                disabled={
                  initiateRestoreMutation.isPending ||
                  (!restoreForm.target_vm_id && restoreForm.restore_type === 'full') ||
                  (!restoreForm.new_vm_name && restoreForm.restore_type === 'clone')
                }
                className="flex items-center space-x-2 rounded-lg bg-purple-600 px-6 py-2 text-white hover:bg-purple-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {initiateRestoreMutation.isPending && (
                  <LoaderIcon className="size-4 animate-spin" />
                )}
                <span>
                  {initiateRestoreMutation.isPending ? 'Restaurando...' : 'Iniciar Restauração'}
                </span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalhes */}
      {showDetailsModal && selectedSnapshot && (
        <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4">
          <div className="mobile-modal-surface w-full max-w-2xl rounded-lg bg-white">
            <div className="border-b p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Detalhes do Snapshot</h3>
                <button
                  onClick={() => setShowDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XIcon className="size-5" />
                </button>
              </div>
            </div>

            <div className="space-y-6 p-6">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-2">
                <div>
                  <h4 className="mb-3 text-sm font-medium text-gray-700">Informações Básicas</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Nome:</span>
                      <span className="font-medium">{selectedSnapshot.name}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">VM ID:</span>
                      <span className="font-mono text-xs">{selectedSnapshot.vm_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Tamanho:</span>
                      <span className="font-medium">
                        {selectedSnapshot.size_bytes
                          ? formatBytes(selectedSnapshot.size_bytes)
                          : selectedSnapshot.vm_storage_gb == null
                            ? '-'
                            : `${formatGB(selectedSnapshot.vm_storage_gb)} GB`}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Status:</span>
                      {getStatusBadge(selectedSnapshot.status)}
                    </div>
                  </div>
                </div>

                <div>
                  <h4 className="mb-3 text-sm font-medium text-gray-700">Configurações Técnicas</h4>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Criptografia:</span>
                      <span
                        className={`font-medium ${
                          selectedSnapshot.is_encrypted ? 'text-green-600' : 'text-gray-600'
                        }`}
                      >
                        {selectedSnapshot.is_encrypted ? 'Habilitada' : 'Desabilitada'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Verificado:</span>
                      <span
                        className={`font-medium ${
                          selectedSnapshot.is_verified ? 'text-green-600' : 'text-gray-600'
                        }`}
                      >
                        {selectedSnapshot.is_verified ? 'Sim' : 'Não'}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Protegido:</span>
                      <span className="font-medium">
                        {selectedSnapshot.protected ? 'Sim' : 'Não'}
                      </span>
                    </div>
                    {selectedSnapshot.expires_at && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Expira:</span>
                        <span className="font-medium">
                          {formatDate(selectedSnapshot.expires_at)}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              <div>
                <h4 className="mb-2 text-sm font-medium text-gray-700">Descrição</h4>
                <p className="rounded-lg bg-gray-50 p-3 text-sm text-gray-600">
                  {selectedSnapshot.description || 'Sem descrição'}
                </p>
              </div>

              <div>
                <h4 className="mb-2 text-sm font-medium text-gray-700">Timeline</h4>
                <div className="space-y-1 rounded-lg bg-gray-50 p-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Criado em:</span>
                    <span className="font-medium">{formatDate(selectedSnapshot.backup_time)}</span>
                  </div>
                  {selectedSnapshot.started_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Iniciado em:</span>
                      <span className="font-medium">{formatDate(selectedSnapshot.started_at)}</span>
                    </div>
                  )}
                  {selectedSnapshot.completed_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Concluído em:</span>
                      <span className="font-medium">
                        {formatDate(selectedSnapshot.completed_at)}
                      </span>
                    </div>
                  )}
                  {selectedSnapshot.duration_seconds && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Duração:</span>
                      <span className="font-medium">
                        {formatDuration(selectedSnapshot.duration_seconds)}
                      </span>
                    </div>
                  )}
                  {selectedSnapshot.checksum && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Checksum:</span>
                      <span className="font-mono text-xs">{selectedSnapshot.checksum}</span>
                    </div>
                  )}
                  {selectedSnapshot.error_message && (
                    <div className="mt-3 rounded border border-red-200 bg-red-50 p-3">
                      <div className="flex items-start space-x-2">
                        <AlertCircleIcon className="mt-0.5 size-4 text-red-600" />
                        <div>
                          <div className="text-sm font-medium text-red-800">Erro:</div>
                          <div className="text-sm text-red-700">
                            {selectedSnapshot.error_message}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex justify-end border-t bg-gray-50 p-6">
              <button
                onClick={() => setShowDetailsModal(false)}
                className="rounded-lg bg-gray-600 px-4 py-2 text-white hover:bg-gray-700"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Alert Details Modal */}
      {showAlertDetailsModal && selectedAlert && (
        <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4">
          <div className="mobile-modal-surface w-full max-w-3xl rounded-lg bg-white">
            <div className="border-b p-6">
              <div className="flex items-start justify-between">
                <div>
                  <div className="mb-2 flex items-center space-x-3">
                    <span className="text-3xl">{getAlertSeverityIcon(selectedAlert.severity)}</span>
                    <h3 className="text-xl font-semibold text-gray-900">{selectedAlert.title}</h3>
                  </div>
                  <div className="flex items-center space-x-2">
                    <span
                      className={`rounded-full px-3 py-1 text-sm font-medium ${getAlertSeverityColor(selectedAlert.severity)}`}
                    >
                      {getAlertSeverityLabel(selectedAlert.severity)}
                    </span>
                    <span
                      className={`rounded-full px-3 py-1 text-sm font-medium ${getAlertStatusColor(selectedAlert.status)}`}
                    >
                      {getAlertStatusLabel(selectedAlert.status)}
                    </span>
                  </div>
                </div>
                <button
                  onClick={() => setShowAlertDetailsModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XIcon className="size-6" />
                </button>
              </div>
            </div>

            <div className="space-y-6 p-6">
              <div>
                <h4 className="mb-2 text-sm font-medium text-gray-700">Mensagem</h4>
                <p className="text-gray-900">{selectedAlert.message}</p>
              </div>

              {selectedAlert.vm_name && (
                <div>
                  <h4 className="mb-2 text-sm font-medium text-gray-700">Máquina Virtual</h4>
                  <div className="flex items-center space-x-2 text-gray-900">
                    <ServerIcon className="size-5" />
                    <span>{selectedAlert.vm_name}</span>
                  </div>
                </div>
              )}

              <div>
                <h4 className="mb-3 text-sm font-medium text-gray-700">Métricas</h4>
                <div className="grid grid-cols-2 gap-4 rounded-lg bg-gray-50 p-4">
                  {selectedAlert.baseline_value !== null && (
                    <div>
                      <div className="mb-1 text-xs text-gray-600">Storage Baseline</div>
                      <div className="text-lg font-semibold text-gray-900">
                        {formatStorageGB(selectedAlert.baseline_value)}
                      </div>
                    </div>
                  )}
                  {selectedAlert.current_value !== null && (
                    <div>
                      <div className="mb-1 text-xs text-gray-600">Storage Atual</div>
                      <div className="text-lg font-semibold text-gray-900">
                        {formatStorageGB(selectedAlert.current_value)}
                      </div>
                    </div>
                  )}
                  {selectedAlert.change_percentage !== null && (
                    <div>
                      <div className="mb-1 text-xs text-gray-600">Crescimento</div>
                      <div
                        className={`text-lg font-semibold ${
                          selectedAlert.change_percentage >= 100
                            ? 'text-red-600'
                            : selectedAlert.change_percentage >= 75
                              ? 'text-yellow-600'
                              : 'text-blue-600'
                        }`}
                      >
                        {formatGrowthPercentage(selectedAlert.change_percentage)}
                      </div>
                    </div>
                  )}
                  {selectedAlert.threshold_exceeded !== null && (
                    <div>
                      <div className="mb-1 text-xs text-gray-600">Threshold Excedido</div>
                      <div className="text-lg font-semibold text-gray-900">
                        {selectedAlert.threshold_exceeded.toFixed(0)}%
                      </div>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <h4 className="mb-2 text-sm font-medium text-gray-700">Timeline</h4>
                <div className="space-y-2 rounded-lg bg-gray-50 p-4 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Detectado em:</span>
                    <span className="font-medium text-gray-900">
                      {formatAlertDate(selectedAlert.detected_at)}
                    </span>
                  </div>
                  {selectedAlert.acknowledged_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Reconhecido em:</span>
                      <span className="font-medium text-gray-900">
                        {formatAlertDate(selectedAlert.acknowledged_at)}
                      </span>
                    </div>
                  )}
                  {selectedAlert.resolved_at && (
                    <div className="flex justify-between">
                      <span className="text-gray-600">Resolvido em:</span>
                      <span className="font-medium text-gray-900">
                        {formatAlertDate(selectedAlert.resolved_at)}
                      </span>
                    </div>
                  )}
                </div>
              </div>

              {selectedAlert.resolution_notes && (
                <div>
                  <h4 className="mb-2 text-sm font-medium text-gray-700">Notas de Resolução</h4>
                  <p className="rounded-lg bg-gray-50 p-3 text-gray-900">
                    {selectedAlert.resolution_notes}
                  </p>
                </div>
              )}
            </div>

            <div className="flex justify-between border-t bg-gray-50 p-6">
              <button
                onClick={() => setShowAlertDetailsModal(false)}
                className="rounded-lg bg-gray-600 px-4 py-2 text-white hover:bg-gray-700"
              >
                Fechar
              </button>

              {selectedAlert.status === 'active' && canManageAlerts && (
                <div className="flex space-x-3">
                  <button
                    onClick={() => dismissAlertMutation.mutate(selectedAlert.id)}
                    className="rounded-lg bg-gray-400 px-4 py-2 text-white hover:bg-gray-500"
                  >
                    Descartar
                  </button>
                  <button
                    onClick={() => acknowledgeAlertMutation.mutate(selectedAlert.id)}
                    className="rounded-lg bg-blue-600 px-4 py-2 text-white hover:bg-blue-700"
                  >
                    Reconhecer
                  </button>
                  <button
                    onClick={() => {
                      setShowAlertDetailsModal(false)
                      setShowResolveModal(true)
                    }}
                    className="rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700"
                  >
                    Resolver
                  </button>
                </div>
              )}

              {selectedAlert.status === 'acknowledged' && canManageAlerts && (
                <button
                  onClick={() => {
                    setShowAlertDetailsModal(false)
                    setShowResolveModal(true)
                  }}
                  className="rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700"
                >
                  Resolver
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Resolve Alert Modal */}
      {showResolveModal && selectedAlert && (
        <div className="bg-opacity-50 fixed inset-0 z-50 flex items-center justify-center bg-black p-4">
          <div className="w-full max-w-md rounded-lg bg-white">
            <div className="border-b p-6">
              <h3 className="text-lg font-semibold text-gray-900">Resolver Alerta</h3>
            </div>

            <div className="p-6">
              <label className="mb-2 block text-sm font-medium text-gray-700">
                Notas de Resolução (opcional)
              </label>
              <textarea
                value={resolutionNotes}
                onChange={(e) => setResolutionNotes(e.target.value)}
                rows={4}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:ring-2 focus:ring-purple-500"
                placeholder="Descreva como o problema foi resolvido..."
              />
            </div>

            <div className="flex justify-end space-x-3 border-t bg-gray-50 p-6">
              <button
                onClick={() => {
                  setShowResolveModal(false)
                  setResolutionNotes('')
                  setShowAlertDetailsModal(true)
                }}
                className="rounded-lg bg-gray-600 px-4 py-2 text-white hover:bg-gray-700"
              >
                Cancelar
              </button>
              <button
                onClick={() =>
                  resolveAlertMutation.mutate({
                    alertId: selectedAlert.id,
                    notes: resolutionNotes || undefined,
                  })
                }
                className="rounded-lg bg-green-600 px-4 py-2 text-white hover:bg-green-700"
              >
                Confirmar Resolução
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modals */}
      <ConfirmModal
        isOpen={showRestoreConfirm}
        onClose={() => setShowRestoreConfirm(false)}
        onConfirm={confirmRestore}
        title="Confirmar Restauração"
        message={
          restoreForm.restore_type === 'clone'
            ? `Será criada uma nova VM "${restoreForm.new_vm_name || 'clone'}" a partir do backup.\n\n${
                restoreForm.restore_network_config
                  ? '⚠️ A configuração de IP original será MANTIDA. Se a VM original estiver ativa, haverá conflito de IP na rede.'
                  : '✅ O IP será resetado para DHCP. A VM clone receberá um novo IP automaticamente.'
              }\n\nNovos endereços MAC serão gerados automaticamente.`
            : 'Deseja iniciar a restauração completa? A VM atual será SUBSTITUÍDA. Esta ação não pode ser desfeita.'
        }
        confirmText="Restaurar"
        variant={
          restoreForm.restore_type === 'clone' && restoreForm.restore_network_config
            ? 'danger'
            : 'warning'
        }
        isLoading={initiateRestoreMutation.isPending}
      />

      <ConfirmModal
        isOpen={!!snapshotToDelete}
        onClose={() => setSnapshotToDelete(null)}
        onConfirm={() => {
          if (snapshotToDelete) deleteSnapshotMutation.mutate(snapshotToDelete)
          setSnapshotToDelete(null)
        }}
        title="Deletar Snapshot"
        message="Deseja deletar este snapshot? Esta ação não pode ser desfeita."
        confirmText="Deletar"
        variant="danger"
        isLoading={deleteSnapshotMutation.isPending}
      />
    </div>
  )
}

export default BackupsPage
